// Havana, "Waves over the Malecón": a warm, windy night on the Malecón in Centro Habana.
// Where you stand: on the land-side pavement of the Malecón a few blocks west of the Paseo del Prado, by the portales of
// a corner building, looking east-northeast (bearing ~70°) along the curve of the sea wall towards the harbour mouth.
// Left (north) is the open sea; across the harbour mouth, at the end of the curve, the Castillo de los Tres Reyes del
// Morro stands long and low on its rock with its lighthouse a third of the way along (Fl(2) W 15 s); right of it the
// dark ridge of La Cabaña, then La Punta and the row of Centro Habana facades along the far curve, nearer and taller
// towards the right. Cheat: El Capitolio's dome really stands about 1 km inland up the Prado, to the south-east, and is
// composed here over the roofs of the far curve, at about its true size for that distance.
// Back to front: deep sky, stars, the moon, two wisps -> the Morro's beam -> El Capitolio's floodlit dome -> the Morro,
// La Cabaña, the far curve's facades and a hand-painted rooftop sign (the billboard) -> the bay (moon path, lamp
// streaks, whitecaps, spray over the far wall) -> the near sea wall with people sitting on it, the seaside pavement and
// its sodium lamps -> the Malecón road with vintage cars -> the wet land-side pavement mirroring it all -> the corner
// building: a cafeteria and a domino table under its portales, a balcony with a rocking chair and laundry above, a
// pigeon loft and water tank on the roof -> vignette.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 140;                                   // sea horizon
const DOME = { cx: 300, top: 17 };                // El Capitolio's cupola: axis column and the finial's tip
const MOON = { x: 178, y: 44, r: 6 };
const MORRO = { x0: 64, x1: 196, wall: 119, rock: 128, water: 142 };   // main curtain: ends, wall top, rock top, waterline
const LH = { x: 110, top: 101 };                 // Morro lighthouse: axis column (the 6 px tower is x-2..x+3), finial row
const FAR = { x0: 250, x1: 398 };                 // the far curve's facades, La Punta to the corner building
const WALL = 172;                                 // near sea wall: cap rows 172-173, land face 174-182
const PROM = 183, KERB = 189;                     // seaside pavement 183-188, kerb
const ROAD0 = 190, LANE_F = 198, LANE_N = 205;    // road from 190; wheel rows of the far (eastbound) and near lanes
const PAVE = 207;                                 // the land-side pavement and the portal floor
const BLD = { x0: 398, x1: 480, roof: 58 };       // the corner building (cozy corner)
const COLS = [400, 430, 460];                     // its portal columns (4 px wide, left edges)
const BB = { x: 339, y: 96, w: 50, h: 20 };       // the hand-painted rooftop sign's face (the pitch is painted exactly here)
const LAMPS = [46, 156, 262, 372];                // sodium lamps on the seaside pavement (pole columns)
const LAMP_TOP = 141;                             // lantern row of those lamps
const NEAR_LAMP = { x: 26, top: 146, foot: 262 };  // the cast-iron lamp on our pavement: shaft column, lantern row, base row

/* ================= Pixel helpers ================= */
const MIXC = {};
/** #rrggbb between two #rrggbb colours (cached) */
function mixHex(a, b, t) {
  t = clamp(t, 0, 1);
  const k = a + b + t.toFixed(3);
  if (MIXC[k]) return MIXC[k];
  const A = hex(a), B = hex(b), c = A.map((v, i) => Math.round(lerp(v, B[i], t)));
  return (MIXC[k] = '#' + c.map(v => v.toString(16).padStart(2, '0')).join(''));
}
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h || !d[(y * w + i) * 4 + 3]) ? null
    : '#' + [0, 1, 2].map(k => d[(y * w + i) * 4 + k].toString(16).padStart(2, '0')).join('');
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// pick from a ramp with ordered dither: v in [0, 1]
function pick(ramp, v, i, y) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
// integer Bresenham line through a put()
function pline(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
// signs: FONT3 with wider M, W, N, K and V (FONT3's read as H or Π at 1x; shapes after ui/postcard.js)
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = (s, gap = 1) => [...s].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + gap, 0) - (s.length ? gap : 0);
/** sign text through a put(): whole pixels; tall draws every row twice (condensed hand-painted capitals) */
function signText(put, s, x, y, col, { gap = 1, tall = false, shade = null } = {}) {
  let cx = Math.round(x); y = Math.round(y);
  const k = tall ? 2 : 1;
  for (const ch of s) {
    const g = glyphOf(ch), w = g?.[0].length ?? 3;
    if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') for (let q = 0; q < k; q++) put(cx + i, y + j * k + q, shade && q === k - 1 && j === g.length - 1 ? shade : col); });
    cx += w + gap;
  }
}

/* ================= Sky: a deep tropical night, cool over the sea, a warm sodium haze over the city ================= */
const SKY = ['#04060f', '#050915', '#070c1c', '#091024', '#0b142b', '#0e1932', '#111e39', '#142440', '#172a47',
  '#1b304d', '#1f3652', '#243d57', '#2a445b'];
const HAZE = ['#05070f', '#080a18', '#0c0e20', '#111228', '#16152e', '#1c1832', '#231b36', '#2b1f39', '#33233b',
  '#3c273d', '#462c3f', '#503141', '#5a3743'];
const SKY_W = [0, 0.25, 0.5, 0.75, 1].map(w => SKY.map((c, k) => mixHex(c, HAZE[k], w)));   // ramps from cool to hazy
function renderSky() {
  const p = painter(W, HZ + 2);
  for (let y = 0; y < HZ + 2; y++) for (let i = 0; i < W; i++) {
    const v = Math.pow(y / (HZ + 1), 1.35);
    // the city's sodium glow: over the far curve and the dome, strongest low on the right
    const g = clamp(1 - Math.hypot((i - 350) / 210, (y - HZ) / 95), 0, 1);
    const moon = Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.16;
    const warm = clamp(g * g * 1.25, 0, 1) * 4, lv = Math.min(4, Math.floor(warm) + (frac(warm) > bayer(i + 2, y + 1) ? 1 : 0));
    const col = pick(SKY_W[lv], v + moon * (1 - g) + g * 0.05, i, y);
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(305);
  while (STARS.length < 70) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 118);
    const nearMoon = Math.hypot(x - MOON.x, y - MOON.y) < 16;
    const inDome = x > DOME.cx - 34 && x < DOME.cx + 34 && y > DOME.top - 3;
    const lowCity = x > 230 && y > 70 + (x - 230) * 0.05;
    if (nearMoon || inDome || (lowCity && r() < 0.8) || (x < 120 && y < 40 && r() < 0.5)) continue;
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: 0.25 + r() * 0.6 * (1 - y / 160), big: r() < 0.06 });
  }
}
function renderMoon() {
  const { r } = MOON, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.7], [1, 2, 1.4], [2, -2, 1.1], [-1, 3, 0.9]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fffaea' : '#f6eccf';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = '#ddd0b2';
    if (dd > r - 1.1) col = dx + dy > 1 ? '#c8b896' : '#efe2c2';
    p.put(i, y, col);
  }
  return p.done();
}
// two soft wisps, moonlit along their tops (dithered, no outline)
function renderWisp(w, h, seed) {
  const r = rng(seed), p = painter(w, h), puffs = [];
  for (let i = 0; i < 9; i++) puffs.push({ x: 8 + r() * (w - 16), y: h * (0.45 + r() * 0.2), rx: 6 + r() * 14, ry: 1.6 + r() * 1.8 });
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    let v = 0;
    for (const q of puffs) v = Math.max(v, 1 - ((i - q.x) / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2);
    v *= Math.min(1, i / 12, (w - 1 - i) / 12);
    if (v * 1.5 > bayer(i, y) + 0.12) p.put(i, y, y < h * 0.48 ? '#3a4a6e' : v > 0.55 ? '#26304e' : '#1c2440');
  }
  return p.done();
}
const WISPS = [{ x: 18, y: 60, w: 120, h: 9, seed: 41 }, { x: 196, y: 14, w: 74, h: 7, seed: 57 }, { x: 392, y: 76, w: 70, h: 7, seed: 63 },
  { x: 110, y: 32, w: 96, h: 7, seed: 71 }, { x: 300, y: 98, w: 84, h: 6, seed: 83 }, { x: 40, y: 4, w: 110, h: 6, seed: 89 }];

/* ================= El Capitolio's cupola (the hero), floodlit warm white ================= */
// Massing after the real one (photos of 2019 after the restoration), rows from the finial down, as shares of the
// cupola's 102 rows (real shares in brackets): lantern 17 (0.16), ribbed shell 25 (0.24), plinth 5, upper drum with its
// arched windows 16 and balustrade 2 (0.18), entablature 6, colonnade 19 and its base 3 (0.29), podium 9 (0.09).
// The colonnade is 63 wide: cupola height to colonnade width 1.62 (real 1.65), shell height to it 0.40 (real 0.40).
const DT = DOME.top, DX = DOME.cx;
const DR = { fin: DT, cap: DT + 2, capC: DT + 6, lcol: DT + 7, lbase: DT + 14, shell: DT + 17, plinth: DT + 42,
  drumC: DT + 47, drum: DT + 50, drumB: DT + 61, bal: DT + 63, ent: DT + 65, col: DT + 71, colB: DT + 90, pod: DT + 93, bot: DT + 101 };
const STONE = ['#fffbf0', '#fff2d8', '#f7e4bc', '#e9cfa0', '#d2b588', '#b39573', '#8c7463', '#655456', '#433a48'];
const PANEL = ['#68708e', '#555d7c', '#454c6a', '#383d5a', '#2c304a', '#22253c'];
const GOLD = ['#fff4c8', '#ffe09a', '#fac26a', '#e09a4e', '#b8703c', '#844c34', '#56342e'];
const RIM = '#c4cce4';                                         // the moon's cool light on the cupola's left edge
const DOME_BOX = { x: DX - 45, y: DT, w: 91, h: DR.bot + 1 - DT };    // canvas of the cupola (its building is in renderCapitolio)
/** floodlit stone on a drum of half-width hw at column offset dx: v (0 light .. 1 dark) */
function drumShade(dx, hw, lift = 0) {
  const u = clamp(dx / (hw + 0.5), -1, 1), f = Math.sqrt(1 - u * u);
  return clamp(0.86 - f * 0.78 + u * 0.12 - lift, 0, 1);
}
function renderDome() {
  const B = DOME_BOX, p = painter(B.w, B.h), put = (x, y, c) => p.put(x - B.x, y - B.y, c);
  const span = (y, hw, fn) => { for (let dx = -hw; dx <= hw; dx++) put(DX + dx, y, fn(dx)); };
  const stone = (dx, hw, y, lift = 0) => pick(STONE, drumShade(dx, hw, lift) * 0.9, DX + dx, y);
  // podium: plain drum with a ring of small arched windows
  for (let y = DR.pod; y <= DR.bot; y++) span(y, 31, dx => {
    if (y === DR.pod) return '#5e5050';
    const a = Math.asin(clamp(dx / 31.5, -1, 1)), k = Math.round(a / (TAU / 22)), wx = Math.round(31 * Math.sin(k * TAU / 22));
    if (y >= DR.pod + 3 && y <= DR.pod + 5 && Math.abs(dx - wx) < (Math.abs(dx) > 24 ? 0.6 : 1) && Math.abs(dx) < 29) return y === DR.pod + 3 ? '#8c6248' : '#4a3236';
    return stone(dx, 31, y, 0.1);
  });
  // colonnade base: a step, its face, its shadow
  for (let y = DR.colB; y < DR.pod; y++) span(y, 30, dx => y === DR.colB ? pick(STONE, 0.12 + Math.abs(dx) / 70, DX + dx, y)
    : y === DR.colB + 1 ? stone(dx, 30, y) : pick(STONE, 0.62 + Math.abs(dx) / 80, DX + dx, y));
  // the peristyle: the drum's lit wall glowing gold between 28 columns (15 in view)
  for (let y = DR.col; y < DR.colB; y++) {
    const j = y - DR.col;
    span(y, 29, dx => {
      const u = dx / 29.5, a = Math.asin(clamp(u, -1, 1));
      const win = Math.abs(frac(a / (TAU / 28)) - 0.5) < 0.26 && j > 2 && j < 17;   // tall windows behind each bay
      const v = (win ? 0.05 : 0.38) + Math.abs(u) ** 2 * 0.45 + (j < 2 ? 0.35 : 0) + (j > 16 ? 0.12 : 0);
      return pick(GOLD, v, DX + dx, y);
    });
    for (let c = 0; c < 28; c++) {
      const a = (c + 0.5) * TAU / 28 - TAU / 4;
      if (Math.cos(a) <= 0.05) continue;
      const s = Math.sin(a), cx = DX + 30 * s, wide = Math.abs(s) < 0.82;
      const x0 = Math.round(cx - (wide ? 0.5 : 0)), v = clamp(0.06 + Math.abs(s) * 0.45 + (j === 0 ? -0.06 : 0), 0, 1);
      put(x0, y, pick(STONE, v + (s > 0.3 ? 0.15 : 0), x0, y));
      if (wide) put(x0 + 1, y, pick(STONE, v + 0.28, x0 + 1, y));
      if (j === 0 || j === 18) { put(x0 - 1, y, pick(STONE, v + 0.2, x0, y)); if (wide) put(x0 + 2, y, pick(STONE, v + 0.35, x0, y)); }
    }
    put(DX - 30, y, j % 3 ? '#e9cfa0' : RIM); put(DX + 30, y, '#8c7463');
  }
  // entablature: cornice, drip shadow, frieze, architrave, shadow on the capitals
  for (let y = DR.ent; y < DR.col; y++) {
    const j = y - DR.ent;
    span(y, 31, dx => j === 0 ? pick(STONE, 0.02 + Math.abs(dx) / 60, DX + dx, y) : j === 1 ? pick(STONE, 0.55 + Math.abs(dx) / 90, DX + dx, y)
      : j === 4 ? pick(STONE, 0.2 + Math.abs(dx) / 70, DX + dx, y) : j === 5 ? '#655456' : stone(dx, 31, y, 0.05));
  }
  put(DX - 31, DR.ent, RIM); put(DX - 31, DR.ent + 2, RIM);
  // balustrade over the colonnade: rail and balusters
  for (let y = DR.bal; y < DR.ent; y++) span(y, 29, dx => y === DR.bal ? pick(STONE, 0.1 + Math.abs(dx) / 60, DX + dx, y)
    : ((DX + dx) & 1) ? pick(STONE, 0.3 + Math.abs(dx) / 70, DX + dx, y) : '#6e4c3c');
  // upper drum: cornice, a wall of 14 arched windows (7 in view) between paired pilasters, its plain base
  for (let y = DR.drumC; y < DR.bal; y++) {
    const j = y - DR.drumC, hw = j < 3 ? 24 : 23;
    span(y, hw, dx => {
      if (j === 0) return pick(STONE, 0.04 + Math.abs(dx) / 50, DX + dx, y);
      if (j === 1) return pick(STONE, 0.5 + Math.abs(dx) / 80, DX + dx, y);
      if (j >= 14) return j === 15 ? '#86705e' : stone(dx, hw, y, 0.12);
      const u = dx / (hw + 0.5), a = Math.asin(clamp(u, -1, 1)), q = a / (TAU / 14), d = Math.abs(q - Math.round(q));
      const ww = 0.17 * (1 - Math.abs(u) * 0.45);
      if (j >= 4 && j <= 12 && d < ww && !(j === 4 && d > ww * 0.4)) return j === 12 ? '#c88a52' : j > 9 ? '#6a4434' : '#3e2c34';
      if (Math.abs(d - 0.5) < 0.05) return pick(STONE, 0.05 + Math.abs(u) * 0.5, DX + dx, y);   // pilasters catch the light
      return stone(dx, hw, y);
    });
    if (j > 1) put(DX - hw, y, RIM);
  }
  // plinth band at the shell's foot: ledge, a ring of small openings, shadow
  for (let y = DR.plinth; y < DR.drumC; y++) {
    const j = y - DR.plinth;
    span(y, 23, dx => j === 0 ? pick(STONE, 0.05 + Math.abs(dx) / 45, DX + dx, y) : j === 4 ? '#655456'
      : (j === 2 && ((DX + dx) % 4 === 0) && Math.abs(dx) < 21) ? '#433a48' : stone(dx, 23, y, 0.05));
  }
  // the shell: 16 pale ribs (9 in view) over dark panels, the ribs closing up into a pale ring under the lantern
  for (let y = DR.shell; y < DR.plinth; y++) {
    const k = DR.plinth - 1 - y, hw = 22.5 * Math.sqrt(Math.max(0, 1 - (k / 26.6) ** 2)), lat = Math.asin(Math.min(1, k / 26.6));
    const h = Math.round(hw);
    span(y, h, dx => {
      const u = clamp(dx / (hw + 0.4), -1, 1), a = Math.asin(u), q = a / (TAU / 16), d = Math.abs(q - Math.round(q)) * (TAU / 16);
      const rib = d < 0.075 / Math.cos(lat) || k > 22 || k < 1;
      const light = clamp(0.55 + 0.45 * Math.cos(a + 0.3), 0, 1) * (1 - 0.32 * k / 24);
      if (Math.abs(dx) === h && h > 3) return dx < 0 ? RIM : '#4a4258';
      if (rib) return pick(STONE, 0.08 + (1 - light) * 0.75 + (k < 1 ? 0.15 : 0), DX + dx, y);
      return pick(PANEL, 0.1 + (1 - light) * 0.85, DX + dx, y);
    });
  }
  // lantern: balustraded base, ten columns (six in view) with its light between them, a small dome, the finial
  for (let y = DR.lbase; y < DR.shell; y++) {
    const j = y - DR.lbase, hw = j === 0 ? 5 : 6;
    span(y, hw, dx => j === 0 ? pick(STONE, 0.15 + Math.abs(dx) / 14, DX + dx, y) : j === 1 ? pick(STONE, Math.abs(dx) / 16, DX + dx, y)
      : ((DX + dx) & 1) ? pick(STONE, 0.25 + Math.abs(dx) / 14, DX + dx, y) : '#7a5a44');
  }
  for (let y = DR.lcol; y < DR.lbase; y++) span(y, 5, dx => (dx & 1) ? pick(STONE, 0.3 + Math.abs(dx) / 11 + (y === DR.lcol ? 0.2 : 0), DX + dx, y)
    : y === DR.lcol ? '#c88a52' : Math.abs(dx) === 4 ? '#f6c878' : '#fff2c0');
  span(DR.capC, 5, dx => pick(STONE, 0.05 + Math.abs(dx) / 9, DX + dx, DR.capC));
  [2, 3, 4, 4].forEach((hw, j) => span(DR.cap + j, hw, dx => dx === -hw ? RIM : pick(PANEL, 0.15 + (dx + hw) / (2 * hw + 1) * 0.6, DX + dx, DR.cap + j)));
  put(DX, DR.fin, '#ffe8a0'); put(DX, DR.fin + 1, '#f2c470'); put(DX - 1, DR.fin + 1, '#b08048'); put(DX + 1, DR.fin + 1, '#8a6040');
  return p.done();
}

/* ================= El Morro across the harbour mouth: long and low on its rock, the lighthouse a third along ================= */
// Seen from the Malecón (photos from the wall in Centro Habana): the curtain wall's top is a near-flat line about 0.18
// of its length above the water, the rock under it about 0.6 of that; the lighthouse rises 17 px over the walls (0.13
// of the curtain's length, real ~0.125) and is 6 px wide; a guard house sits on the walls right of it.
const MST = ['#f6d09a', '#e8b47c', '#d39864', '#b97c52', '#996446', '#784e40', '#583c3a', '#3c2c34'];   // floodlit limestone
const ROCK = ['#6a4c42', '#54403e', '#42343a', '#342a34', '#28212e', '#1e1a28'];
const MORRO_MASS = [                               // [x0, x1, wall top] left to right; faces alternate lit / turned away
  [62, 66, 121], [66, 92, 119], [92, 124, 119], [124, 151, 118], [151, 178, 119], [178, 196, 120], [196, 211, 123], [211, 227, 126],
];
function morroRock(x) {                            // top row of the rock (or the land under the walls) at column x
  if (x < 50) return 999;
  if (x < 57) return Math.round(142 - (x - 50) * 1.2);
  if (x < 63) return Math.round(133.6 - (x - 57) * 1.1);
  if (x < 200) return MORRO.rock + Math.round(Math.sin(x * 0.37) * 0.6 + Math.sin(x * 0.11) * 0.8);
  if (x < 252) return Math.round(129 + (x - 200) * 0.06 + Math.sin(x * 0.3) * 0.8);
  return 999;
}
function renderMorro(p) {
  const { put } = p;
  // La Cabaña's ridge beyond the Morro to the harbour mouth: dark trees, the fortress's long lit wall low on it
  for (let x = 190; x < 256; x++) {
    const top = Math.round(126 + (x - 190) * 0.075 + Math.sin(x * 0.55) * 1.2 + Math.sin(x * 1.7) * 0.6);
    for (let y = top; y < MORRO.water; y++) put(x, y, y === top ? '#2a2a3a' : pick(['#1c1a28', '#181624', '#14121e'], (y - top) / 14, x, y));
    if (x > 214 && x < 250) { put(x, 136, x % 5 ? '#8a5a40' : '#c08050'); put(x, 137, '#4a3632'); }
  }
  // the rock: dark, its ledges catching the floodlights, surf at its foot
  for (let x = 50; x < 228; x++) {
    const top = morroRock(x);
    for (let y = top; y < MORRO.water; y++) {
      const d = y - top, ledge = hash(x >> 2, y, 7) < 0.18 && d > 1;
      put(x, y, ledge ? '#7a5848' : pick(ROCK, 0.1 + d * 0.06 + hash(x, y, 9) * 0.2, x, y));
    }
  }
  // the walls: sloped faces, courses, embrasures along the top, a lit pool at each floodlight
  MORRO_MASS.forEach(([x0, x1, top], m) => {
    for (let x = x0; x < x1; x++) {
      const rk = morroRock(x), turned = m % 2 === 1;
      for (let y = top; y < rk; y++) {
        const f = (y - top) / Math.max(1, rk - top);
        let v = 0.42 - f * 0.28 + (turned ? 0.16 : 0) + (y % 3 === 0 ? 0.06 : 0) + hash(x, y, 11) * 0.08;
        v -= Math.max(0, 1 - Math.abs((x - x0) - (x1 - x0) / 2) / 14) * 0.1 * f;
        put(x, y, pick(MST, v, x, y));
      }
      put(x, top, (x - x0) % 9 === 4 && top < 122 ? '#4a3634' : pick(MST, turned ? 0.18 : 0.06, x, top));   // coping and embrasures
    }
    put(x0, top, '#f6d09a');
  });
  // the seaward corner's garita (sentry box) with its little dome
  for (let y = 116; y < 121; y++) for (let x = 62; x < 65; x++) put(x, y, y === 116 ? (x === 63 ? '#e8b47c' : null) : y === 117 ? '#d39864' : x === 62 ? '#f6d09a' : '#b97c52');
  // the guard house on the walls
  for (let y = 112; y < 118; y++) for (let x = 137; x < 149; x++) put(x, y, y === 112 ? '#f6d09a' : (y === 114 || y === 115) && (x === 140 || x === 145) ? '#3c2c34' : pick(MST, 0.22 + (y - 112) * 0.05 + (x > 146 ? 0.15 : 0), x, y));
  // the lighthouse: tower (three small windows), corbelled gallery, the lantern (its light is drawn per frame), cap
  const L = LH.x;
  for (let y = 106; y < MORRO.wall + 1; y++) for (let x = L - 2; x <= L + 3 + (y > 112 ? 1 : 0); x++) {
    const u = (x - L - 0.5) / 3.5, win = x === L && (y === 108 || y === 112 || y === 116);
    put(x, y, win ? '#4a3234' : pick(MST, 0.08 + u * u * 0.5 + (u > 0.3 ? 0.18 : 0) + (y - 106) * 0.012, x, y));
  }
  for (let x = L - 3; x <= L + 4; x++) { put(x, 105, x === L - 3 ? '#e8b47c' : '#996446'); put(x, 104, (x & 1) ? '#583c3a' : '#2a2030'); }
  for (let y = 101; y < 104; y++) for (let x = L - 1; x <= L + 2; x++) put(x, y, x === L - 1 || x === L + 2 ? '#3a2c34' : '#fff6d8');
  put(L, 100, '#b04a3a'); put(L + 1, 100, '#7a3430'); put(L, 99, '#5a3430');                // the lantern's red cap
  // surf on the rock's foot
  for (let x = 48; x < 230; x++) if (hash(x, 3, 13) < 0.45) put(x, MORRO.water - 1, hash(x, 4, 13) < 0.5 ? '#c8d4e0' : '#7a8aa0');
}

// El Capitolio's body under its cupola, floodlit a step below the cupola: the central pavilion's attic and roof
// balustrade, and the long wings to either side with their balustraded roof terrace, rising behind the far curve's
// roofs (it stands about 1 km inland) so the cupola stands on its own building, not on the houses
const CAPB = ['#f4e2bc', '#e2c89e', '#c8ac84', '#a68c6c', '#80695a', '#5a4a4c', '#3e3440'];
const CAP = { roof: DR.bot + 1, wing: DR.bot + 7, x0: 246, x1: 378 };
function renderCapitolio(p) {
  const { put } = p, lit = (x, y, v) => put(x, y, pick(CAPB, v, x, y));
  const wall = (x0, x1, top) => {
    for (let x = x0; x <= x1; x++) for (let y = top; y < HZ + 2; y++) {
      const j = y - top, pil = (x - x0) % 7 === 0, win = (x - x0) % 7 >= 2 && (x - x0) % 7 <= 4 && j >= 4 && (j - 4) % 6 < 3;
      if (j === 0) lit(x, y, 0.02 + Math.abs(x - DX) / 900);
      else if (j === 1) put(x, y, CAPB[5]);
      else if (j === 2) put(x, y, (x & 1) ? CAPB[1] : CAPB[3]);                       // dentils
      else if (win) put(x, y, hash(x >> 2, y >> 2, 61) < 0.3 ? '#e8b060' : '#4a3438');
      else lit(x, y, (pil ? 0.08 : 0.22) + j * 0.012 + hash(x, y, 62) * 0.06);
    }
  };
  // the wings: a roof balustrade on top, then the cornice and two floors of windows between pilasters
  wall(CAP.x0, DX - 41, CAP.wing); wall(DX + 41, CAP.x1, CAP.wing);
  for (const [a, b] of [[CAP.x0, DX - 41], [DX + 41, CAP.x1]]) for (let x = a; x <= b; x++) {
    put(x, CAP.wing - 2, CAPB[2]); put(x, CAP.wing - 1, (x - a) % 5 === 0 ? CAPB[2] : (x & 1) ? CAPB[3] : null);
  }
  for (const ex of [CAP.x0, CAP.x1 - 9]) for (let x = ex; x < ex + 10; x++) for (let y = CAP.wing - 3; y < CAP.wing; y++) lit(x, y, 0.15 + (y - CAP.wing + 3) * 0.1);   // end pavilions
  // the central pavilion, taller, carrying the drum; its roof balustrade either side of the podium
  wall(DX - 40, DX + 40, CAP.roof);
  for (const [a, b] of [[DX - 40, DX - 32], [DX + 32, DX + 40]]) for (let x = a; x <= b; x++) { put(x, CAP.roof - 2, CAPB[1]); if (x & 1) put(x, CAP.roof - 1, CAPB[2]); }
  for (let y = CAP.roof; y < HZ + 2; y++) { put(DX - 40, y, CAPB[1]); put(DX + 40, y, CAPB[4]); }
}

/* ================= The far curve: La Punta and the facades of Centro Habana along the Malecón ================= */
const farWallY = x => Math.round(143 + 9 * Math.pow(clamp((x - FAR.x0) / (FAR.x1 - FAR.x0), 0, 1), 1.5));   // cap of the far sea wall
// [x0, width, height, colour, kind]: eclectic houses with portales, nearer and taller towards the right
const FACADES = [
  [263, 10, 10, '#d8c8a0', 'house'], [273, 11, 12, '#d6788a', 'house'], [284, 10, 11, '#6aae94', 'house'], [294, 12, 13, '#d6a452', 'house'],
  [306, 10, 11, '#7c9cd6', 'house'], [316, 12, 13, '#e88e7a', 'house'], [328, 10, 12, '#4cb0aa', 'house'], [338, 52, 27, '#bfa27a', 'block'],
  [390, 9, 26, '#e07c98', 'house'],
];
const FAR_WINS = [];                                 // windows that switch on and off: { id, x, y, col, n, ph }
const NIGHT = '#141428', SODIUM = '#ffb060';
function renderFacade(p, [x0, w, h, col, kind], r) {
  const { put } = p, base = farWallY(x0 + (w >> 1)) - 2, top = base - h + 1, x1 = x0 + w - 1;
  const body = [mixHex(col, '#fff4dc', 0.25), col, mixHex(col, NIGHT, 0.3), mixHex(col, NIGHT, 0.55), mixHex(col, NIGHT, 0.72)];
  const storey = kind === 'block' ? 5 : 4;
  for (let y = top; y <= base; y++) for (let x = x0; x <= x1; x++) {
    const f = (base - y) / h;                                     // the street's sodium light fades up the front
    put(x, y, pick(body, 0.3 + f * 0.62 + (x === x1 ? 0.25 : 0) + (x === x0 ? -0.15 : 0), x, y));
  }
  // cornice and parapet (a small pediment on some houses)
  for (let x = x0; x <= x1; x++) { put(x, top, mixHex(col, '#fff4dc', 0.1 + 0.2 * (x === x0))); put(x, top + 1, body[4]); }
  if (kind === 'house' && r() < 0.6) { const c = x0 + (w >> 1); put(c, top - 1, body[2]); put(c - 1, top - 1, body[3]); put(c + 1, top - 1, body[3]); }
  // portales at street level: columns and lamplit arches
  const pt = base - 4;
  for (let x = x0; x <= x1; x++) {
    const colm = (x - x0) % 3 === 0;
    for (let y = pt; y < base; y++) put(x, y, colm ? mixHex(col, '#ffd8a0', 0.35) : y === pt ? body[3] : r() < 0.25 ? '#ffc070' : '#6a4030');
    put(x, pt - 1, body[1]);
  }
  // upper storeys: tall doors onto balconies (iron rails as a dotted dark line), some lit
  for (let y = pt - storey; y > top + 2; y -= storey) {
    for (let x = x0 + 1; x < x1; x += 3) {
      const v = r(), lit = v < 0.35, wc = lit ? (r() < 0.75 ? '#ffcf7a' : '#ffe6a8') : v < 0.75 ? mixHex(col, NIGHT, 0.82) : '#2a2232';
      put(x, y, wc); put(x, y + 1, lit ? '#e8a456' : wc);
      if (lit && r() < 0.4) FAR_WINS.push({ id: FAR_WINS.length, x, y, col: wc, n: 2 + Math.floor(r() * 5), ph: r() });
      if (kind === 'block' || r() < 0.7) put(x, y + 2, '#2a2230');
    }
    if (kind === 'block') for (let x = x0; x <= x1; x++) put(x, y + 3, mixHex(col, '#fff4dc', 0.15));   // the block's balcony slabs
  }
}
function renderFar() {
  const p = painter(W, H), r = rng(77);
  renderMorro(p);
  renderCapitolio(p);
  // La Punta's little fort at the end of the curve: low lit walls, a garita, the Prado's trees behind it
  for (let x = FAR.x0; x < 263; x++) {
    const tb = farWallY(x) - 2;
    for (let y = tb - 6; y < tb - 3; y++) if (hash(x, y, 21) < 0.8) put2(p, x, y, pick(['#2a3428', '#202a22', '#18201c'], hash(x, y, 22), x, y));
    for (let y = tb - 3; y <= tb; y++) put2(p, x, y, pick(MST, 0.3 + (tb - y) * 0.08, x, y));
  }
  p.put(256, farWallY(256) - 7, '#e8b47c'); p.put(256, farWallY(256) - 6, '#b97c52');
  for (const f of FACADES) renderFacade(p, f, r);
  // the far road, the sea wall's lit cap and dark face, and the lamp posts along it (lanterns drawn per frame)
  for (let x = FAR.x0; x < FAR.x1 + 1; x++) {
    const yw = farWallY(x);
    p.put(x, yw - 1, '#2a2430'); p.put(x, yw, '#b8a488'); p.put(x, yw + 1, '#4a3c40'); p.put(x, yw + 2, '#2a2634');
  }
  for (const lx of FAR_LAMPS) { const yw = farWallY(lx); for (let y = yw - 5; y < yw; y++) p.put(lx, y, '#3a3040'); }
  return p.done();
}
const put2 = (p, x, y, c) => p.put(x, y, c);
const FAR_LAMPS = [258, 274, 290, 306, 322, 338, 354, 370, 386];

/* ================= The bay: dark blue-green, lighter at the horizon, whitecaps rolling in towards the wall ================= */
const SEA = ['#24485a', '#1e4052', '#19384a', '#143042', '#10283a', '#0c2132', '#091a29', '#071420'];
const seaTop = x => x < FAR.x0 ? HZ : Math.max(HZ, farWallY(Math.min(x, FAR.x1)) + 3);
function renderSea() {
  const p = painter(W, WALL - HZ + 1);
  for (let x = 0; x < W; x++) {
    const t0 = seaTop(x);
    for (let y = t0; y <= WALL; y++) {
      const d = (y - HZ) / (WALL - HZ), moon = Math.max(0, 1 - Math.abs(x - MOON.x) / 70) * 0.18 * (1 - d);
      const city = x > FAR.x0 ? 0.08 * (1 - (y - t0) / 12) : 0;
      let v = Math.pow(d, 0.8) - moon - city;
      const swell = Math.sin(x * 0.09 + y * 1.9) + Math.sin(x * 0.031 - y * 0.7);   // long low swells
      v += swell * 0.04;
      p.put(x, y - HZ, pick(SEA, v, x, y));
    }
  }
  return p.done();
}
// whitecaps: crests rolling in from the bay towards the wall, nearer ones longer and brighter (per frame in phase 2)
const CRESTS = [];
{
  const r = rng(808);
  for (let i = 0; i < 46; i++) {
    const y = HZ + 3 + Math.floor(Math.pow(r(), 0.7) * (WALL - HZ - 5)), d = (y - HZ) / (WALL - HZ);
    const x = Math.floor(r() * W);
    if (y < seaTop(x) + 1) continue;
    CRESTS.push({ x, y, len: Math.round(2 + d * 9 + r() * 4), ph: r(), k: 3 + Math.floor(r() * 4) });
  }
}

/* ================= The near sea wall, the seaside pavement, the Malecón road and the wet land-side pavement ================= */
const WALLC = ['#d8c8ac', '#bcaa90', '#9c8a78', '#7c6c64', '#5e5254', '#463e46', '#342e38', '#26222c'];
const lampLight = (x, y, ly) => Math.max(...LAMPS.map(lx => Math.max(0, 1 - Math.hypot((x - lx - 4) / 46, (y - ly) / 26))));
function renderNear() {
  const p = painter(W, H - WALL), put = (x, y, c) => p.put(x, y - WALL, c);
  for (let x = 0; x < W; x++) {
    // the wall: a lit cap where people sit, its weathered land-side face with joints and stains
    const lc = lampLight(x, WALL, WALL);
    put(x, WALL, pick(WALLC, 0.3 - lc * 0.3 + hash(x, 1, 31) * 0.12, x, WALL));
    put(x, WALL + 1, pick(WALLC, 0.5 - lc * 0.25, x, WALL + 1));
    for (let y = WALL + 2; y < PROM; y++) {
      const joint = x % 34 === 7, stain = hash(x >> 1, (y - WALL) >> 2, 32) < 0.2;
      const v = 0.68 - lampLight(x, y, WALL + 6) * 0.35 + (y - WALL) * 0.012 + (stain ? 0.1 : 0) + (joint ? 0.14 : 0) + hash(x, y, 33) * 0.08;
      put(x, y, pick(WALLC, v, x, y));
    }
    // seaside pavement: big square slabs, a pool of sodium under each lamp
    for (let y = PROM; y < KERB; y++) {
      const slab = (x + (y - PROM) * 2) % 22 === 0 || y === PROM + 3;
      const v = 0.78 - lampLight(x, y, PROM + 3) * 0.45 + (slab ? 0.08 : 0) + hash(x, y, 34) * 0.05;
      put(x, y, pick(WALLC, v, x, y));
    }
    put(x, KERB, pick(WALLC, 0.35 - lampLight(x, KERB, KERB) * 0.2, x, KERB));
    // the road: wet asphalt, a worn dashed lane line, sheen under each lamp
    for (let y = ROAD0; y <= LANE_N; y++) {
      const ln = (y === 199 || y === 200) && x % 18 < 9 && hash(x >> 2, 3, 35) < 0.8;
      const sheen = lampLight(x, y, ROAD0 + 6);
      const base = ['#2a2630', '#221f2a', '#1c1a24', '#17151f', '#131119'];
      put(x, y, ln ? (y === 199 ? '#6a625c' : '#4a4448') : pick(base, 0.55 + (y - ROAD0) * 0.02 - sheen * 0.5 + hash(x, y, 36) * 0.1, x, y));
    }
    // the land-side kerb and the wet pavement in front of us (its mirror is drawn per frame)
    put(x, LANE_N + 1, '#6a5c56'); put(x, LANE_N + 2, '#2a2228');
    for (let y = PAVE + 1; y < H; y++) {
      // big square slabs in perspective: rows deepen and joints fan out from a vanishing point over the bay
      const v = 1500 / (y - 128), v1 = 1500 / (y + 1 - 128), row = Math.floor(v), joint = row !== Math.floor(v1);
      const u = (x - 236) / (y - 128) * 4.2, vj = Math.floor(u) !== Math.floor((x + 1 - 236) / (y - 128) * 4.2);
      const glint = !joint && Math.floor(1500 / (y - 1 - 128)) !== row && hash(Math.floor(u), row, 38) < 0.6;   // lit front edges
      put(x, y, joint || vj ? '#16121a' : glint ? '#463a42' : pick(['#3a3240', '#322a38', '#2a2430', '#231e29', '#1c1822'], 0.25 + (y - PAVE) / 80 + hash(Math.floor(u), row, 37) * 0.25, x, y));
    }
  }
  return p.done();
}
// sodium lamps on the seaside pavement: a tall pole, a curved arm, the lantern (lit per frame)
function renderLamps() {
  const p = painter(W, H);
  for (const lx of LAMPS) {
    for (let y = LAMP_TOP + 2; y < KERB - 1; y++) { p.put(lx, y, '#2c2832'); p.put(lx + 1, y, y > PROM - 6 ? '#4a3c38' : '#1a1820'); }
    p.put(lx - 1, KERB - 2, '#2c2832'); p.put(lx + 2, KERB - 2, '#1a1820');          // base flare
    for (let y = KERB - 6; y < KERB - 2; y++) p.put(lx - 1, y, '#3a3440');
    p.put(lx, LAMP_TOP + 1, '#2c2832'); p.put(lx + 1, LAMP_TOP, '#2c2832'); p.put(lx + 2, LAMP_TOP - 1, '#2c2832');
    for (let i = 3; i < 8; i++) p.put(lx + i, LAMP_TOP - 1, '#3a3440');                 // the arm out over the road
    for (let i = 4; i < 9; i++) p.put(lx + i, LAMP_TOP, i === 4 || i === 8 ? '#2c2832' : '#5a4a40');   // the head's housing
  }
  return p.done();
}

/* ================= The corner building: portales with a cafeteria and a domino table, a lived-in balcony above ================= */
// Faded turquoise eclectic house of four storeys: a portal of three arches on Tuscan columns (the covered pavement of
// Centro Habana), two floors of tall louvred doors under stained-glass fanlights (vitrales) onto iron balconies, a
// moulded cornice and balustraded parapet, and on the roof a water tank, an antenna and a pigeon loft.
const TURQ = ['#9ad8cc', '#72bcb2', '#52a09a', '#3e8482', '#2f6a6c', '#245458', '#1c4048', '#152e38'];
const CREAM = ['#fbefd4', '#e6d4ae', '#c8b28c', '#a08a6c', '#786656', '#544640'];
const IRON = '#120e16', IRON_L = '#5e4a40';
const BAYS = COLS.map(c => c + 4);                                // left edge of each bay (to the next column or the frame)
const BAY_C = [417, 447, 473];                                   // door axis of each bay above the portal
const FLOOR1 = { sill: 150, door: 117, fan: 110, rail: 138 };     // the balcony floor, door tops, fanlights, rail
const FLOOR2 = { sill: 103, door: 76, fan: 69, rail: 94 };
const ARCH = { crown: 163, spring: 175 };
const VITRAL = ['#e85a4a', '#f2c84a', '#4a86e0', '#5ab46a', '#f2c84a', '#e85a4a', '#4a86e0'];
function renderBuilding() {
  const p = painter(W, H), { put } = p;
  const wallV = (x, y) => {                                     // the facade's paint: night above, warm light from below and the left lamp
    const lamp = Math.max(0, 1 - Math.hypot((x - 378) / 40, (y - 150) / 70)) * 0.3, low = clamp((y - 110) / 100, 0, 1) * 0.18;
    return 0.62 - lamp - low + (hash(x >> 1, y >> 2, 41) < 0.15 ? 0.1 : 0) + hash(x, y, 42) * 0.06;
  };
  for (let y = BLD.roof; y < PAVE + 1; y++) for (let x = BLD.x0; x < BLD.x1; x++) put(x, y, pick(TURQ, wallV(x, y), x, y));
  for (let y = BLD.roof; y < PAVE + 1; y++) { put(BLD.x0, y, pick(TURQ, 0.4, BLD.x0, y)); put(BLD.x0 + 1, y, pick(TURQ, 0.48, BLD.x0, y)); }   // the corner's lit edge
  // parapet: coping, posts over the columns, balusters
  for (let x = BLD.x0; x < BLD.x1; x++) {
    put(x, 50, CREAM[1]); put(x, 51, CREAM[3]);
    const post = COLS.some(c => x >= c && x < c + 4) || x >= 477;
    for (let y = 52; y < 57; y++) put(x, y, post ? pick(CREAM, 0.3 + (x % 4) * 0.1, x, y) : (x & 1) ? (y === 52 || y === 56 ? CREAM[3] : pick(CREAM, 0.35, x, y)) : null);
    put(x, 57, CREAM[2]);
  }
  // cornice: lit top, dentils, a deep shadow under it
  for (let x = BLD.x0; x < BLD.x1; x++) {
    put(x, BLD.roof, CREAM[0]); put(x, BLD.roof + 1, CREAM[1]); put(x, BLD.roof + 2, (x & 1) ? CREAM[2] : CREAM[4]);
    put(x, BLD.roof + 3, '#1a2830'); put(x, BLD.roof + 4, '#22343c'); put(x, BLD.roof + 6, CREAM[3]);
  }
  // a string course between the floors
  for (let x = BLD.x0; x < BLD.x1; x++) { put(x, 104, CREAM[1]); put(x, 105, CREAM[2]); put(x, 106, '#1c3038'); }
  // doors under fanlights, two floors
  for (const F of [FLOOR2, FLOOR1]) BAY_C.forEach((cx, b) => {
    const x0 = cx - 6, x1 = cx + 5;
    for (let y = F.fan - 2; y < F.sill; y++) { put(x0 - 1, y, CREAM[2]); put(x1 + 1, y, CREAM[3]); }   // the surround
    for (let x = x0 - 1; x <= x1 + 1; x++) put(x, F.fan - 2, CREAM[1]);
    put(cx, F.fan - 3, CREAM[0]); put(cx - 1, F.fan - 3, CREAM[1]);                                     // keystone
    for (let y = F.fan; y < F.door; y++) for (let x = x0; x <= x1; x++) {                                // the vitral's fan of coloured glass
      const dx = x - cx + 0.5, dy = F.door - y, a = Math.atan2(dy, dx), seg = Math.floor(a / Math.PI * 7);
      if (Math.hypot(dx, dy) > 6.6) { put(x, y, pick(TURQ, 0.5, x, y)); continue; }
      put(x, y, Math.hypot(dx, dy) < 1.6 ? '#fff0c0' : (x + y) % 5 === 0 ? '#3a2a30' : VITRAL[(seg + b) % 7]);
    }
    for (let x = x0; x <= x1; x++) put(x, F.door - 1, '#3a2a30');
    const open = F === FLOOR1 && b === 0;
    for (let y = F.door; y < F.sill; y++) for (let x = x0; x <= x1; x++) {
      if (open) {                                                // an open door: the lamplit room behind
        const e = Math.min(x - x0, x1 - x, 9) / 6, v = 0.15 + (1 - Math.min(1, e)) * 0.5 + (y - F.door) * 0.006;
        put(x, y, x - x0 < 2 || x1 - x < 2 ? (y & 1 ? '#3e6e58' : '#2a5040') : pick(['#ffe0a0', '#f6c47a', '#d8985a', '#a8683e', '#6e4430'], v, x, y));
      } else {
        const slat = (y - F.door) % 2 === 0, leak = F === FLOOR1 && b === 1 && slat && x !== cx;
        put(x, y, x === cx ? '#1a3a2c' : leak ? '#c89850' : slat ? '#3f7a62' : '#2a5846');
      }
    }
  });
  // the open door's room: a ceiling fan, a picture, a lace curtain
  const ox = BAY_C[0];
  for (let x = ox - 3; x <= ox + 3; x++) put(x, FLOOR1.door + 4, '#4a3024');
  put(ox, FLOOR1.door + 2, '#4a3024'); put(ox, FLOOR1.door + 3, '#4a3024');
  for (let y = FLOOR1.door + 9; y < FLOOR1.door + 14; y++) for (let x = ox + 1; x < ox + 5; x++) put(x, y, y === FLOOR1.door + 9 || x === ox + 1 ? '#7a4a2a' : '#4a6a8a');
  for (let y = FLOOR1.door; y < FLOOR1.door + 22; y++) { put(ox - 4, y, '#fff2dc'); if (y < FLOOR1.door + 14) put(ox - 3, y, '#f0d8b0'); }
  // iron balconies: the second floor's balconettes, the first floor's long balcony with scrolls
  BAY_C.forEach(cx => {
    for (let x = cx - 8; x <= cx + 7; x++) {
      put(x, FLOOR2.rail, IRON); put(x, FLOOR2.rail - 1, x === cx - 8 ? IRON_L : null);
      for (let y = FLOOR2.rail + 1; y < FLOOR2.sill; y++) if ((x - cx) % 2 === 0) put(x, y, IRON);
      put(x, FLOOR2.sill, CREAM[1]); put(x, FLOOR2.sill + 1, CREAM[4]);
    }
  });
  for (let x = BLD.x0; x < BLD.x1; x++) {
    put(x, FLOOR1.rail, IRON_L); put(x, FLOOR1.rail + 1, IRON);
    for (let y = FLOOR1.rail + 2; y < FLOOR1.sill; y++) {
      const m = (x - BLD.x0) % 6, j = y - FLOOR1.rail - 2;
      if (m === 0 || (j === 5) || ((m === 2 || m === 4) && (j === 2 || j === 8)) || (m === 3 && (j === 3 || j === 7))) put(x, y, IRON);
    }
    put(x, FLOOR1.sill, CREAM[0]); put(x, FLOOR1.sill + 1, CREAM[2]); put(x, FLOOR1.sill + 2, '#2a3a40');
  }
  // the portal: an entablature over three arches on columns, the lamplit arcade behind
  for (let x = BLD.x0; x < BLD.x1; x++) { put(x, 154, CREAM[1]); put(x, 155, CREAM[2]); put(x, 159, CREAM[3]); put(x, 160, '#1c3038'); }
  COLS.forEach((c, i) => {
    const a0 = c + 4, a1 = (COLS[i + 1] ?? BLD.x1 + 4) - 1, acx = (a0 + a1) / 2, r = (a1 - a0 + 1) / 2;
    for (let x = a0; x <= Math.min(a1, BLD.x1 - 1); x++) for (let y = ARCH.crown; y < PAVE - 1; y++) {
      const inArch = y >= ARCH.spring || Math.hypot(x - acx, (y - ARCH.spring) * 1.1) < r;
      if (!inArch) continue;
      const edge = !(y >= ARCH.spring || Math.hypot(x - acx, (y - ARCH.spring) * 1.1) < r - 1.2);
      const v = 0.35 + clamp((ARCH.spring + 4 - y) / 14, 0, 1) * 0.4 + Math.abs(x - acx) / r * 0.15;
      put(x, y, edge ? CREAM[2] : pick(['#e8a868', '#c88452', '#9a6040', '#6e4232', '#46302c', '#2a2026'], v, x, y));
    }
    for (let y = 161; y < PAVE - 1; y++) for (let k = 0; k < 4; k++) put(c + k, y, pick(CREAM, 0.12 + k * 0.17 + (y > 200 ? 0.15 : 0), c + k, y));
    for (let k = -1; k < 5; k++) { put(c + k, 161, CREAM[0]); put(c + k, 162, CREAM[2]); put(c + k, PAVE - 2, CREAM[1]); put(c + k, PAVE - 1, CREAM[3]); }
  });
  // the portal floor's tiles and the step down to the pavement
  for (let x = BLD.x0 - 2; x < BLD.x1; x++) {
    put(x, PAVE - 1, ((x >> 1) + 0) & 1 ? '#8a6a56' : '#5a4440'); put(x, PAVE, ((x >> 1) + 1) & 1 ? '#7a5c4c' : '#4a3a38');
    put(x, PAVE + 1, '#6a5a52'); put(x, PAVE + 2, '#2a2228'); put(x, PAVE + 3, '#221c24');
  }
  // the cafeteria in the middle bay: its painted board, shelves of cups and bottles, the counter
  const b2 = BAYS[1];
  for (let y = 177; y < 184; y++) for (let x = b2 + 4; x < b2 + 22; x++) put(x, y, y === 177 || y === 183 ? '#8a3a2a' : '#f0e2c0');
  signText(put, 'CAFE', b2 + 6, 178, '#c8302e');
  for (let x = b2 + 2; x < b2 + 24; x++) { put(x, 187, '#5a3a2a'); if (hash(x, 5, 43) < 0.6) put(x, 186, ['#f0f0e8', '#e05a4a', '#f2c84a', '#7ac0e0'][Math.floor(hash(x, 6, 43) * 4)]); }
  for (let x = b2 + 1; x < b2 + 25; x++) { put(x, 194, '#d8b080'); put(x, 195, '#8a5a3a'); for (let y = 196; y < PAVE - 2; y++) put(x, y, (x - b2) % 6 === 0 ? '#2e5a7a' : pick(['#4a86b0', '#3a6e98', '#2e5a7a'], (y - 196) / 10, x, y)); }
  // the right bay: a tall street door (zaguán), dimly lit stairs inside
  const b3 = BAYS[2];
  for (let y = 172; y < PAVE - 2; y++) for (let x = b3 + 3; x < b3 + 14; x++) put(x, y, x === b3 + 3 || y === 172 ? '#3a2420' : pick(['#7a5034', '#4e3428', '#2e2026'], (x - b3 - 3) / 11 + (y - 172) / 60, x, y));
  // the roof: pigeon loft (left), antenna, water tank (right)
  for (let y = 41; y < 50; y++) for (let x = 406; x < 428; x++) put(x, y, y === 41 ? '#8a6a4a' : (x - 406) % 4 === 0 ? '#4a3428' : (y === 45 && (x - 406) % 4 === 2) ? '#120e14' : pick(['#a0805a', '#7a5c40', '#5a4230'], (y - 41) / 9 + (x > 424 ? 0.3 : 0), x, y));
  for (let x = 404; x < 430; x++) { const y = 40 - Math.floor((x - 404) / 9); put(x, y, '#5e4a3e'); put(x, y + 1, '#3a2c28'); }
  for (let y = 26; y < 50; y++) put(441, y, '#2a2630');
  for (let x = 436; x < 447; x++) { put(x, 29, '#2a2630'); if (x > 437 && x < 445) put(x, 33, '#2a2630'); }
  for (let y = 34; y < 50; y++) for (let x = 452; x < 468; x++) {
    if (y >= 46 && !(x === 453 || x === 466)) continue;
    const u = (x - 459.5) / 8;
    put(x, y, y === 34 ? '#6a6670' : y >= 46 ? '#2a2630' : pick(['#9a96a0', '#7a7682', '#5a5662', '#3e3c48'], 0.2 + u * u * 0.5 + (u > 0.2 ? 0.3 : 0) + (y % 4 === 0 ? 0.15 : 0), x, y));
  }
  return p.done();
}

/* ================= The billboard: a hand-painted ice-cream sign on a rooftop frame of the far curve ================= */
// Cuba has almost no commercial hoardings; old painted signs on rooftop frames do survive. Its own ad: HELADOS DE COCO
// with a two-scoop cone, faded yellow paint in a red border, lit by one gooseneck lamp. The frame stands on the roof
// of the sand-coloured house left of the corner building; render(t, { pitch }) paints the pitch on the face instead.
const BB_ROOF = 121;                                             // that house's roof row
const BB_LAMP = { x: BB.x + 25, y: BB.y - 4 };                   // the lamp's lens
const BB_FLICK = [[58.4, 59.6], [171.2, 172.9]];                 // it stutters now and then
function renderBillboard() {
  const x0 = BB.x - 2, y0 = BB.y - 7, p = painter(BB.w + 4, BB_ROOF - y0 + 1), put = (x, y, c) => p.put(x - x0, y - y0, c);
  const WOOD = '#3a2a24', WOOD_L = '#6a4e3e';
  for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { put(x, BB.y - 1, WOOD_L); put(x, BB.y + BB.h, WOOD); put(x, BB.y + BB.h + 1, '#241a1c'); }
  for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { put(BB.x - 1, y, WOOD_L); put(BB.x + BB.w, y, WOOD); }
  for (const lx of [BB.x + 4, BB.x + BB.w - 5]) for (let y = BB.y + BB.h + 2; y < BB_ROOF; y++) { put(lx, y, WOOD); put(lx + 1, y, '#1c1418'); }
  pline(put, BB.x + 5, BB.y + BB.h + 2, BB.x + BB.w - 6, BB_ROOF - 1, '#2a1e20');
  pline(put, BB.x + 5, BB_ROOF - 1, BB.x + BB.w - 6, BB.y + BB.h + 2, '#2a1e20');
  // the gooseneck lamp over the middle of the face
  for (let y = BB_LAMP.y + 1; y < BB.y - 1; y++) put(BB_LAMP.x - 2, y, '#2a2226');
  put(BB_LAMP.x - 1, BB_LAMP.y, '#2a2226'); for (let x = BB_LAMP.x - 1; x <= BB_LAMP.x + 1; x++) put(x, BB_LAMP.y - 1, '#4a3a34');
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  const PAINT = ['#fbe8a0', '#f2d888', '#e6c872', '#d4b260'];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.hypot((x - 25) / 30, y / 22);
    p.put(x, y, x === 0 || y === 0 || x === w - 1 || y === h - 1 ? '#c8382e' : x === 1 || y === 1 || x === w - 2 || y === h - 2 ? '#f6e6b4' : pick(PAINT, lamp * 0.9, x, y));
  }
  // the cone: a coconut scoop under a strawberry one, on a waffle cone
  for (let y = 10; y < 18; y++) { const hw = Math.max(0, Math.round((18 - y) * 0.5)); for (let x = 8 - hw; x <= 8 + hw; x++) p.put(x, y, (x + y) % 3 === 0 ? '#a8682e' : x > 8 ? '#c8883e' : '#e0a456'); }
  for (let y = 2; y < 13; y++) for (let x = 3; x < 14; x++) {
    const coco = Math.hypot(x - 8, y - 8.5) < 3.8, fresa = Math.hypot(x - 8, y - 5) < 3.4;
    if (fresa) p.put(x, y, x + y < 11 ? '#ffb0c4' : x > 9 ? '#c8486e' : '#f2708e');
    else if (coco) p.put(x, y, x + y < 15 ? '#fffcf0' : x > 10 ? '#d8ccb0' : '#f2ead6');
  }
  p.put(9, 1, '#d8283e'); p.put(9, 2, '#a01c30');                                   // the cherry
  const c = p.done();
  signText(p.put, 'HELADOS', 14, 2, '#d02c2a', { tall: true, gap: 2, shade: '#8a1c22' });
  signText(p.put, 'DE COCO', 17, 13, '#2a4aa8');
  p.done();
  return gradeAd(c, { level: 0.8, tint: '#141428', mix: 0.12, wear: 0.45, seed: 23 });
}
function bbLampI(t) {
  let v = 0.95 + 0.05 * wave(t, 960);
  if (inEvent(t, BB_FLICK)) { const q = hash(29, Math.floor(t * 20)); v *= q > 0.45 ? 1 : q > 0.2 ? 0.35 : 0.06; }
  return v;
}
function drawBillboard(t, pitch) {
  ctx.drawImage(BB_C, BB.x - 2, BB.y - 7);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(BB_AD, BB.x, BB.y);
    const li = bbLampI(t);
    if (li < 0.9) { ctx.globalAlpha = (0.9 - li) * 0.6; rect(ctx, BB.x, BB.y, BB.w, BB.h, '#140c10'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const li = bbLampI(t);
  rect(ctx, BB_LAMP.x - 1, BB_LAMP.y, 3, 1, li > 0.6 ? '#ffe6b0' : '#6a5040');
  halo(HL.bbLamp, BB_LAMP.x, BB_LAMP.y + 1, li);
}

/* ================= The Morro's light: Fl(2) W 15 s, two beams turning sixteen times a loop ================= */
const BEAM_N = 16, BEAM_GAP = 0.34;                              // turns per loop (15 s each); the two beams' spacing (rad)
function drawBeam(t) {
  const ox = LH.x + 0.5, oy = 102;
  let flash = 0;
  for (const off of [0, BEAM_GAP]) {
    const a = TAU * (BEAM_N * t / LOOP + 0.08) + off, s = Math.sin(a), c = Math.cos(a);
    flash = Math.max(flash, Math.pow(Math.max(0, c), 24));
    const len = 170 * Math.abs(s), alpha = 0.16 * (0.35 + 0.65 * Math.abs(s)) * (c < -0.2 ? 0.55 : 1);
    if (len < 6) continue;
    const ex = ox + len * Math.sign(s), spread = 2 + len * 0.05;
    const g = ctx.createLinearGradient(ox, oy, ex, oy);
    g.addColorStop(0, `rgba(255,246,214,${alpha.toFixed(3)})`); g.addColorStop(1, 'rgba(255,246,214,0)');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.moveTo(ox, oy - 0.8); ctx.lineTo(ex, oy - spread - len * 0.02); ctx.lineTo(ex, oy + spread * 0.6); ctx.lineTo(ox, oy + 0.8); ctx.closePath(); ctx.fill();
  }
  return flash;
}

/* ================= Vintage American cars on the Malecón (1950s convertibles and hardtops) ================= */
const CAR_W = 48, CAR_H = 15;
/** a 1950s American car in profile facing right: tail fins, a chrome spear with a white cove, fender skirts, white walls */
function renderCar({ body, top, cove = '#f4f0e6', hard = false, riders = [] }) {
  const p = painter(CAR_W, CAR_H), { put } = p;
  const B = [mixHex(body, '#ffffff', 0.4), body, mixHex(body, '#1a1020', 0.28), mixHex(body, '#1a1020', 0.55)];
  const topY = x => x < 1 ? 8 : x < 2 ? 5 : x < 7 ? 4 : x < 10 ? 5 : x < 33 ? 6 : x < 45 ? 6 : 7;
  const wheel = (x, y) => Math.min(Math.hypot(x - 10, y - 11.5), Math.hypot(x - 37, y - 11.5));
  for (let x = 0; x < CAR_W - 1; x++) for (let y = topY(x); y < 12; y++) {
    const fArch = Math.hypot(x - 37, y - 11.5) < 3.9 && y > 8, rArch = Math.hypot(x - 10, y - 11.5) < 3.9 && y > 10;   // a skirt over the rear wheel
    if (fArch || rArch) continue;
    let c = y === topY(x) ? B[0] : y === 11 ? B[3] : y === 8 ? '#e8eef4' : y < 8 ? B[1] : B[2];
    if (y >= 9 && y <= 10 && x > 4 && x < 21 && (y === 9 || x > 7)) c = cove;               // the two-tone cove behind the spear
    put(x, y, c);
  }
  for (let y = 7; y < 11; y++) put(CAR_W - 1, y, y === 7 ? '#fff6d0' : '#e8eef4');          // grille and headlamp
  put(CAR_W - 2, 7, '#fff6d0');
  for (let y = 8; y < 11; y++) put(0, y, '#c8d0dc');                                         // rear bumper
  put(1, 5, '#ff4a3a'); put(1, 6, '#ff4a3a'); put(1, 7, '#a82a2a');                           // the fin's tail lamp
  if (hard) {
    for (let x = 18; x < 30; x++) put(x, 1, top);
    for (let y = 2; y < 6; y++) {
      for (let x = 17 - (y >> 1); x < 32; x++) {
        const cp = x === 17 - (y >> 1) || x === 18 - (y >> 1), bp = x === 24, ap = x >= 29 + (y >> 1);
        if (x > 30 + (y >> 1)) continue;
        put(x, y, cp || bp ? top : ap ? '#c8d0dc' : y === 2 ? '#b8d4e4' : '#6a8aa4');
      }
    }
  } else {
    for (let x = 16; x < 20; x++) put(x, 5, '#e8d8bc');                                       // seat backs
    for (let x = 23; x < 27; x++) put(x, 5, '#e8d8bc');
    for (let y = 2; y < 6; y++) put(30 + (y >> 1), y, y === 2 ? '#e8eef4' : '#a8c8dc');      // the windscreen
  }
  for (const [x, skin, hair, shirt] of riders) {
    put(x, 1, hair); put(x + 1, 1, hair); put(x + 2, 1, hair);
    put(x, 2, hair); put(x + 1, 2, skin); put(x + 2, 2, skin); put(x + 1, 3, skin); put(x + 2, 3, skin);
    for (let k = -1; k < 4; k++) put(x + k, 4, shirt); for (let k = -1; k < 4; k++) put(x + k, 5, mixHex(shirt, '#1a1020', 0.3));
  }
  for (let y = 8; y < CAR_H; y++) for (let x = 6; x < 42; x++) {
    const d = wheel(x, y), front = Math.abs(x - 37) < 5;
    if (d < 3.1 && (front || y > 10)) put(x, y, d < 0.8 ? '#9aa4b4' : d < 1.5 ? '#14121a' : d < 2.2 ? '#e2ded6' : '#14121a');
  }
  return p.done();
}
const CARS = [
  { body: '#ff6aa8', riders: [[16, '#c48c60', '#1a1214', '#f4f0e6'], [24, '#8c5a3c', '#1a1214', '#ffd23f']] },
  { body: '#3ec8c0', top: '#f0ece4', hard: true },
  { body: '#e8383a', riders: [[24, '#e2ac82', '#3c2418', '#f4f0e6']] },
  { body: '#f2d27a', top: '#2a6a5a', cove: '#2a6a5a', hard: true },
  { body: '#5a7ad8', top: '#f4f0e6', hard: true },
];

/* ================= People (after rome.js's person(): a head, a body, legs and a colour each, lit by the scene) ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f0c8a0', '#d8a47c', '#b07a52', '#8a5636', '#64402a'], HAIRS = ['#16100e', '#3a2418', '#6a4222', '#c8a060', '#d4d0cc'];
const NIGHT_P = '#141022', GLOWC = '#ffd6a0';
/** a look: proportions for a height of h px, colours dimmed for the night (dk) */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, NIGHT_P, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT_P, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null };
}
/**
 * One person, feet (or seat) on row fy, centred on column x; returns the head's top row.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * (o.dangle: legs hang down in front); o.lean: head px towards a neighbour;
 * o.aL / o.aR: 0 down, 1 up, 2 phone, 3 out, 4 bent up, 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (o.sit && o.dangle) {                                // seated facing us: the legs hang down in front
    const n = o.dangle;
    R(tx, fy - 1, bw, 2, P.bot); R(tx, fy + 1, 2, n, legC); R(tx + bw - 2, fy + 1, 2, n, legC);
    R(tx, fy + n + 1, 2, 1, P.shoe); R(tx + bw - 2, fy + n + 1, 2, 1, P.shoe);
  } else if (o.sit && side) {                                    // seated in profile: thighs forward, shins down
    R(dir > 0 ? x - 1 : x - 4, fy - 1, 5, 2, P.bot); R(dir > 0 ? x + 3 : x - 4, fy + 1, 2, o.shin || 0, legC);
    if (o.shin) R(dir > 0 ? x + 3 : x - 5, fy + 1 + o.shin, 3, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                     // the lamps' light on the shoulders
  if (P.bag && side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag);
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 4) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); R(p0, hy - 2, 2, view === 0 ? 1 : 0, '#ffe2a8'); }
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
const TOPS = ['#e8e4d8', '#d8404a', '#f2c84a', '#3a6ac8', '#2a9a8a', '#e87aa8', '#e8823a', '#4a9a4a', '#8a5ac8', '#58b0d8', '#f4f0e6'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];
/* ----- who is there (still in the concept frame; phase 2 gives each a verb on the timeline) ----- */
const SEAT = WALL, WALK_Y = PROM + 4, PORTAL_Y = PAVE - 2;
const CAST = [
  // on the sea wall, facing the bay: a fisherman with a hand line, a couple, a guitar trio, friends, a girl on her phone
  { x: 84, y: SEAT, rank: 0, P: look(22, 3, 0, '#c8d0dc', BOTS[1], { hat: '#d8c8a0' }), o: { sit: 1 }, prop: 'line' },
  { x: 126, y: SEAT, rank: 0.31, P: look(22, 2, 0, TOPS[3], BOTS[0]), o: { sit: 1 } },
  { x: 133, y: SEAT, rank: 0.31, P: look(21, 1, 1, TOPS[5], BOTS[3], { long: 3 }), o: { sit: 1, lean: -1 }, act: 'lean' },
  { x: 194, y: SEAT, rank: 0, P: look(22, 4, 0, TOPS[10], BOTS[2]), o: { sit: 1, view: 2, dir: 1, aR: 3 }, prop: 'guitar' },
  { x: 206, y: SEAT, rank: 0.12, P: look(22, 3, 0, TOPS[1], BOTS[0]), o: { sit: 1, view: 1, dangle: 7 }, act: 'clap' },
  { x: 215, y: SEAT, rank: 0.12, P: look(21, 0, 3, TOPS[2], BOTS[4], { long: 2 }), o: { sit: 1 } },
  { x: 288, y: SEAT, rank: 0.5, P: look(22, 2, 0, TOPS[4], BOTS[1]), o: { sit: 1, view: 2, dir: 1 }, act: 'chat' },
  { x: 298, y: SEAT, rank: 0.5, P: look(22, 4, 1, TOPS[6], BOTS[0]), o: { sit: 1, view: 2, dir: -1 }, act: 'chat' },
  { x: 346, y: SEAT, rank: 0.21, P: look(21, 1, 0, TOPS[8], BOTS[3], { long: 3 }), o: { sit: 1, view: 1, dangle: 7, aR: 2 } },
  { x: 60, y: SEAT, rank: 0.74, P: look(21, 0, 2, TOPS[7], BOTS[0]), o: { sit: 1 } },
  { x: 250, y: SEAT, rank: 0.42, P: look(21, 3, 0, TOPS[9], BOTS[1]), o: { sit: 1 } },
];
const PORTAL_CAST = [
  // domino under the left arch: two players in profile, one facing us across the table, a neighbour watching
  { x: 421, y: PORTAL_Y - 2, P: look(23, 1, 4, '#e8e4d8', BOTS[0], { dk: 0.12 }), o: { view: 1, aL: 4 } },
  { x: 407, y: PORTAL_Y - 6, P: look(23, 3, 0, TOPS[4], BOTS[1], { dk: 0.12 }), o: { sit: 1, view: 2, dir: 1, shin: 5, aR: 3 } },
  { x: 426, y: PORTAL_Y - 6, P: look(23, 4, 4, TOPS[9], BOTS[2], { dk: 0.12 }), o: { sit: 1, view: 2, dir: -1, shin: 5, aR: 3 } },
  { x: 416, y: PORTAL_Y - 7, P: look(23, 2, 0, TOPS[1], BOTS[0], { dk: 0.12 }), o: { sit: 1, view: 1 } },
  // the cafeteria's woman behind her counter
  { x: 449, y: PORTAL_Y, P: look(22, 3, 0, '#f4f0e6', BOTS[0], { long: 2, dk: 0.1 }), o: { view: 1, aR: 3 } },
];
const ROCKER = { x: 417, y: FLOOR1.sill - 8 };                  // the old woman's rocking chair on the balcony (seat row)
const GRANNY = look(21, 3, 4, '#f4ece0', '#6a4a8a', { skirt: '#6a4a8a', dk: 0.05 });
// the front layers drawn over the people: the first floor's iron balcony and its laundry, the cafeteria's counter
function renderRail() {
  const p = painter(W, H), { put } = p;
  for (let x = BLD.x0; x < BLD.x1; x++) {
    put(x, FLOOR1.rail, IRON_L); put(x, FLOOR1.rail + 1, IRON);
    for (let y = FLOOR1.rail + 2; y < FLOOR1.sill; y++) {
      const m = (x - BLD.x0) % 6, j = y - FLOOR1.rail - 2;
      if (m === 0 || (j === 5) || ((m === 2 || m === 4) && (j === 2 || j === 8)) || (m === 3 && (j === 3 || j === 7))) put(x, y, IRON);
    }
  }
  for (const [x0, x1] of [[468, 476]]) for (let x = x0; x < x1; x++) for (let y = FLOOR1.rail - 7; y < FLOOR1.rail; y++)   // a fern in a pot
    if ((x === x0 + 3 || x === x0 + 4) && y > FLOOR1.rail - 3) put(x, y, '#a8583a'); else if (hash(x, y, 51) < 0.55 && y < FLOOR1.rail - 2) put(x, y, hash(x, y, 52) < 0.5 ? '#3a7a4a' : '#5aa05a');
  for (let x = 433; x < BLD.x1; x++) put(x, 121 + Math.round(Math.sin((x - 433) / 47 * Math.PI) * 2), '#2a2228');   // the laundry line
  return p.done();
}
function renderCounter() {
  const p = painter(24, PAVE - 2 - 194);
  for (let x = 0; x < 24; x++) { p.put(x, 0, '#d8b080'); p.put(x, 1, '#8a5a3a'); for (let y = 2; y < PAVE - 2 - 194; y++) p.put(x, y, x % 6 === 5 ? '#2e5a7a' : pick(['#4a86b0', '#3a6e98', '#2e5a7a'], y / 10, x, y)); }
  return p.done();
}
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 2.4 > bayer(x, y)) p.put(x, y, '#05040a', 70);
  }
  return p.done();
}

const DRIVER = look(27, 3, 0, '#e8e0d0', '#3a3a58', { hat: '#d8c8a0', dk: 0.3 });
const VENDOR = look(28, 4, 0, '#d8404a', '#b09a70', { hat: '#2a2a34', dk: 0.25 });
function drawForegroundLife(t, crowd) {
  if (crowd > 0.18) person(BICI.x + 46, BICI.base - 21, DRIVER, { sit: 1, view: 2, dir: -1 });       // dozing in the seat
  const bi = 0.92 + 0.08 * wave(t, 720);
  R(CART_BULB.x - 1, CART_BULB.y - 2, 3, 3, '#fff2c0'); halo(HL.bulb, CART_BULB.x, CART_BULB.y, bi); halo(HL.pool, CART_BULB.x - 8, CART.base - 6, bi);
  if (crowd > 0.08) person(CART.x - 8, CART.base - 1, VENDOR, { view: 1, aL: 3 });
}

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// The city's own weather is the sea's spray (drawn by the scene); rain, snow and leaves picks fall at three depths:
// far over the bay, mid onto the pavement by the wall, near past the corner building.
const WEATHER = weatherPlans(['spray']);
const WX_LAND = [[HZ + 2, WALL], [PROM, KERB], [PAVE + 4, H + 4]];
const WX_LIGHTS = LAMPS.map(lx => ({ x: lx + 6, y: LAMP_TOP + 6, rx: 26, ry: 34, col: '#ffc890' }))
  .concat([{ x: BAYS[1] + 13, y: 175, rx: 16, ry: 18, col: '#ffd8a0' }, { x: NEAR_LAMP.x, y: NEAR_LAMP.top + 10, rx: 34, ry: 50, col: '#ffd8a0' }]);
const WX_LEAVES = { wind: [0, 30, W, PAVE] };                       // no tree near: leaves blow in on the wind
const WX = { land: WX_LAND, lights: WX_LIGHTS, leaves: WX_LEAVES };
const WX_BB = { land: WX_LAND, leaves: WX_LEAVES, lights: [{ x: BB_LAMP.x, y: BB.y + 6, rx: 26, ry: 14, col: '#ffe6b0' }, ...WX_LIGHTS] };
const CROWD = 0.65;

/* ================= The bay per frame: the Morro and the far curve mirrored, lamp and moon paths, whitecaps ================= */
function drawSea(t) {
  ctx.drawImage(SEA_C, 0, HZ);
  reflect(ctx, t, { top: MORRO.water, rows: 14, x: 48, w: 182, squash: 1.6, alpha: 0.3, amp: 0.5 });
  for (const lx of FAR_LAMPS) streak(ctx, t, lx, 2, '#ffb060', 0.4, lx, farWallY(lx) + 3, WALL);
  streak(ctx, t, LH.x + 0.5, 3, '#fff2d0', 0.3, 5, MORRO.water + 1, WALL);
  streak(ctx, t, MOON.x, 9, '#8ab8c8', 0.35, 3.1, HZ + 1, WALL);
  streak(ctx, t, MOON.x, 4, '#fff2cc', 0.8, 7.7, HZ + 1, WALL);
  for (const c of CRESTS) {
    const u = frac(t * c.k / LOOP + c.ph), a = Math.sin(Math.PI * u);
    if (a < 0.15) continue;
    ctx.globalAlpha = a * (0.35 + 0.5 * (c.y - HZ) / (WALL - HZ));
    rect(ctx, c.x, c.y, c.len, 1, '#a8c4cc');
  }
  ctx.globalAlpha = 1;
}

/* ================= The foreground: a cast-iron lamp on our pavement, framing the left ================= */
// A bicitaxi parked on our pavement (its driver dozing in the passenger seat) and a fruit seller's push cart under a
// hanging bulb: close, big, below the people's band
const BICI = { x: 70, base: 255 };                               // left end (the front wheel) and the ground row
const CART = { x: 306, base: 252 };                              // the cart bed's left end and the ground row
const CART_BULB = { x: CART.x + 41, y: 211 };
function wheelAt(put, cx, cy, r, spokes = 8, rim = '#9aa0ac') {
  for (let y = Math.floor(cy - r - 1); y <= cy + r + 1; y++) for (let x = Math.floor(cx - r - 1); x <= cx + r + 1; x++) {
    const d = Math.hypot(x - cx, y - cy), a = Math.atan2(y - cy, x - cx);
    if (d <= r + 0.5 && d > r - 1.3) put(x, y, d > r - 0.5 ? '#141018' : rim);
    else if (d < r - 1.3 && d > 1.2 && Math.abs(frac(a / TAU * spokes + 0.5) - 0.5) < 0.06) put(x, y, '#5a5e6a');
    else if (d <= 1.2) put(x, y, '#c8ccd4');
  }
}
function shadowOn(put, x0, x1, y) {
  for (let x = x0; x <= x1; x++) for (let k = 0; k < 2; k++) if ((x + k) % 2 === 0 || (x > x0 + 2 && x < x1 - 2)) put(x, y + k, '#0c0a10');
}
function renderBicitaxi(p) {
  const { put } = p, X = BICI.x, G = BICI.base;
  shadowOn(put, X - 2, X + 62, G);
  // the canopy: striped cloth on four thin posts, a scalloped fringe
  for (let x = X + 26; x <= X + 61; x++) for (let y = G - 44; y < G - 39; y++) {
    const stripe = Math.floor((x - X - 26) / 4) & 1, top = y === G - 44;
    if (top && (x === X + 26 || x === X + 61)) continue;
    put(x, y, top ? (stripe ? '#ffe28a' : '#ff8a7a') : stripe ? (y < G - 41 ? '#f2c84a' : '#c89a30') : (y < G - 41 ? '#e8483a' : '#a8302a'));
  }
  for (let x = X + 26; x <= X + 61; x++) if ((x & 1) === 0) put(x, G - 39, (Math.floor((x - X - 26) / 4) & 1) ? '#c89a30' : '#a8302a');
  for (const px0 of [X + 29, X + 58]) for (let y = G - 38; y < G - 18; y++) put(px0, y, '#c8ccd4');
  // the cab: a painted side panel with a flower, the red bench and its back, the floor
  for (let x = X + 28; x <= X + 59; x++) for (let y = G - 18; y < G - 9; y++) {
    const edge = y === G - 18 || y === G - 10 || x === X + 28 || x === X + 59;
    put(x, y, edge ? '#f2d04a' : pick(['#4a7ad8', '#3a64c0', '#2e50a0'], (y - G + 18) / 9 + (x > X + 54 ? 0.3 : 0), x, y));
  }
  const fx = X + 43, fy = G - 14;                                 // a hand-painted flower on the panel
  for (const [dx, dy, c] of [[0, -2, '#ff8ab0'], [-2, 0, '#ff8ab0'], [2, 0, '#ff8ab0'], [0, 2, '#ff8ab0'], [-1, -1, '#ff6a98'], [1, 1, '#ff6a98'], [0, 0, '#ffe24a'], [3, 3, '#4aa860'], [4, 2, '#4aa860']]) put(fx + dx, fy + dy, c);
  for (let x = X + 34; x <= X + 56; x++) { put(x, G - 21, '#e8484a'); put(x, G - 20, '#a82a32'); put(x, G - 19, '#6a1c24'); }
  for (let y = G - 34; y < G - 19; y++) { put(X + 55, y, '#a82a32'); put(X + 56, y, '#6a1c24'); }
  for (let x = X + 28; x <= X + 60; x++) { put(x, G - 9, '#2a2630'); put(x, G - 8, '#141018'); }
  put(X + 60, G - 15, '#ff3a2a'); put(X + 60, G - 14, '#a82020');                                  // rear reflector
  // the bicycle in front: fork, frame, saddle, handlebar, lamp, chain to the cab
  wheelAt(put, X + 7, G - 7, 6.5); wheelAt(put, X + 46, G - 8, 7.5, 10);
  pline(put, X + 7, G - 7, X + 10, G - 24, '#c8ccd4'); pline(put, X + 10, G - 24, X + 23, G - 23, '#d8303a');
  pline(put, X + 11, G - 20, X + 22, G - 8, '#d8303a'); pline(put, X + 22, G - 8, X + 23, G - 23, '#d8303a');
  pline(put, X + 22, G - 8, X + 30, G - 9, '#a8202a');
  for (let x = X + 19; x < X + 25; x++) put(x, G - 25, '#141018');
  for (let x = X + 7; x < X + 13; x++) put(x, G - 27, '#c8ccd4'); put(X + 7, G - 26, '#c8ccd4');
  put(X + 10, G - 22, '#ffe6a8'); put(X + 9, G - 22, '#fff6d0');
  for (let x = X + 20; x < X + 25; x++) put(x, G - 7, '#3a3640');                                     // chain ring and pedal
  put(X + 22, G - 5, '#141018'); put(X + 23, G - 5, '#141018');
}
function renderCart(p) {
  const { put } = p, X = CART.x, G = CART.base;
  shadowOn(put, X - 14, X + 48, G);
  // a pole with the bulb's wire, the push handles, the bed with FRUTAS painted on it, the big wheel and the stand
  for (let y = CART_BULB.y - 6; y < G - 22; y++) { put(X + 44, y, '#3a2a24'); put(X + 45, y, '#1e1418'); }
  for (let x = X + 40; x < X + 45; x++) put(x, CART_BULB.y - 6, '#3a2a24');
  put(CART_BULB.x, CART_BULB.y - 5, '#2a2026'); put(CART_BULB.x, CART_BULB.y - 4, '#2a2026'); put(CART_BULB.x, CART_BULB.y - 3, '#2a2026');
  for (let x = X - 14; x < X; x++) { put(x, G - 18 + Math.round((X - x) / 6), '#6a4a30'); }
  for (let x = X; x <= X + 46; x++) for (let y = G - 22; y < G - 12; y++) {
    const plank = (y - G + 22) % 3 === 0, edge = y === G - 22 || x === X || x === X + 46;
    put(x, y, edge ? '#8a6a40' : plank ? '#1e4a34' : pick(['#3a7a54', '#2e6a4a', '#245a3e'], (y - G + 22) / 10 + (x > X + 40 ? 0.3 : 0), x, y));
  }
  signText(put, 'FRUTAS', X + 11, G - 20, '#f2d04a');
  wheelAt(put, X + 12, G - 8, 7.5, 8, '#b08050');
  for (let y = G - 12; y <= G; y++) { put(X + 41, y, '#4a3426'); put(X + 42, y, '#2a1e1a'); }
  // the fruit piled on the bed: pineapples with their crowns, a bunch of bananas, mangos, papayas, avocados
  const blob = (cx, cy, rx, ry, cols) => { for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let x = Math.floor(cx - rx); x <= cx + rx; x++) { const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2; if (d <= 1) put(x, y, d < 0.3 && x < cx && y < cy ? cols[0] : x > cx + rx * 0.3 || y > cy + ry * 0.4 ? cols[2] : cols[1]); } };
  for (let i = 0; i < 4; i++) blob(X + 7 + i * 11, G - 25, 5.5, 3.2, ['#a8e070', '#5aa04a', '#2e6a34']);        // avocados and limes at the back
  for (let i = 0; i < 5; i++) blob(X + 4 + i * 9, G - 24, 3.4, 2.6, i & 1 ? ['#ffd070', '#ff9a3a', '#c8582a'] : ['#ff9a8a', '#e8483a', '#a82a2a']);   // mangos
  for (const [cx, cy] of [[X + 13, G - 29], [X + 31, G - 30]]) {                                          // pineapples
    blob(cx, cy, 3, 4.5, ['#f2c84a', '#c8902e', '#8a5a1e']);
    for (let y = cy - 5; y < cy + 4; y += 2) for (let x = cx - 2; x <= cx + 2; x += 2) put(x + ((y >> 1) & 1), y, '#6a3a14');
    for (const [dx, dy] of [[0, -6], [0, -7], [-1, -6], [1, -6], [-2, -7], [2, -7], [0, -8], [-1, -8], [1, -9]]) put(cx + dx, cy + dy, (dx + dy) & 1 ? '#3a8a3a' : '#5ab04a');
  }
  for (let k = 0; k < 6; k++) { put(X + 21 + k, G - 28 + (k > 2 ? 1 : 0), '#ffe24a'); put(X + 21 + k, G - 27 + (k > 2 ? 1 : 0), '#d8b02a'); }   // bananas
  put(X + 20, G - 29, '#6a5a2a');
  blob(X + 40, G - 27, 3, 4, ['#ffb07a', '#f2803a', '#7aa04a']);                                        // a papaya
}

function renderForeground() {
  const p = painter(W, H), { put } = p, { x, top, foot } = NEAR_LAMP;
  const IR = ['#5a4a46', '#2e262c', '#1a1418', '#0e0a0e'];
  // the base: a fluted plinth, then the shaft with its rings, the crossarm and the lantern's cage (glass lit per frame)
  for (let y = foot - 12; y <= foot; y++) { const hw = y > foot - 3 ? 4 : y > foot - 8 ? 3 : 2; for (let k = -hw; k <= hw; k++) put(x + k, y, IR[k < 0 ? 1 : k === 0 ? 0 : 2 + (k > 1 ? 1 : 0)]); }
  for (let y = top + 8; y < foot - 12; y++) { put(x - 1, y, IR[0]); put(x, y, IR[1]); put(x + 1, y, IR[2]); }
  for (const ry of [top + 14, top + 40, foot - 30]) for (let k = -2; k <= 2; k++) put(x + k, ry, k < 0 ? IR[0] : IR[2]);
  for (let k = -6; k <= 6; k++) put(x + k, top + 8, Math.abs(k) === 6 ? IR[0] : IR[1]);
  for (const sx of [-6, 6]) { put(x + sx, top + 7, IR[1]); put(x + sx, top + 6, IR[1]); }
  for (let y = top - 6; y < top + 6; y++) for (let k = -3; k <= 3; k++) if (Math.abs(k) === 3 || y === top - 6 || y === top + 5) put(x + k, y, IR[1]);
  for (let k = -2; k <= 2; k++) put(x + k, top - 7, IR[2]); put(x, top - 8, IR[0]); put(x, top - 9, IR[1]);
  renderBicitaxi(p); renderCart(p);
  return p.done();
}

/* ================= The loop's timeline (seconds) =================
   always   the Morro's two beams turn 16 times (15 s); cars cross both lanes; walkers pace the wall; crests roll in;
            small spray bursts along the walls every 10 s; spray mist drifts at three depths; the grandmother rocks
     20, 88, 160, 228  a medium wave slaps the near wall
     48     BIG wave on the near wall at x 118 (swell from 34; the couple and the fisherman flinch)
     62-132 a fishing boat leaves the harbour mouth and heads out past the Morro (horn at 64)
     96-128 the pigeons are let out of the loft and circle the corner building
    126     BIG wave runs along the far curve and bursts in front of the facades at x 322 (swell from 112)
    150-157 the fisherman hauls in a fish
    168     the nine o'clock cannon from La Cabaña: a flash, then its smoke drifts (boom at 169)
    204     BIG wave on the near wall at x 236 (swell from 190; the guitar trio flinch)
   Every period divides 240; every event sits well inside (0, 240).                                                    */
const BIG = [{ t: 48, x: 118, far: false, h: 58 }, { t: 126, x: 322, far: true, h: 34 }, { t: 204, x: 236, far: false, h: 62 }];
const MED = [{ t: 20, x: 62, h: 26 }, { t: 88, x: 300, h: 24 }, { t: 160, x: 172, h: 28 }, { t: 228, x: 352, h: 22 }];
const SWELL = 14;                                                // build-up before each big wave (s)
const BOAT = { t0: 62, t1: 132 };
const PIGEONS = { t0: 96, t1: 128, cx: 438, cy: 30 };
const CANNON = { t: 168, x: 241, y: 134 };
const FISH = [150, 157];
/* ----- spray plumes: a deterministic burst of droplets plus soft puffs, thrown up from the wall's cap ----- */
const DROPS = Array.from({ length: 150 }, (_, i) => ({ vx: (hash(i, 1, 71) - 0.5) * 20, vy: 0.55 + hash(i, 2, 71) * 0.6, s: hash(i, 3, 71) < 0.2 ? 2 : 1, d: hash(i, 4, 71) }));
function plume(t, w, y0) {
  const tau = t - w.t;
  if (tau < -0.1 || tau > 7) return 0;
  const g = w.h * 2.2, H = w.h, wind = 7;
  // soft body: puffs that rise, swell and thin out, drifting with the wind
  for (let k = 0; k < 7; k++) {
    const a = clamp(tau / 0.9, 0, 1), up = H * (0.35 + 0.65 * hash(k, w.t, 72)) * Math.sin(Math.min(1, tau / 1.6) * Math.PI / 2) * (1 - smooth(2.2, 6.5, tau) * 0.6);
    const sz = clamp(w.h / 58, 0.3, 1.1), r = Math.round(((3 + k * 1.3) * (0.5 + 0.5 * a) * (w.far ? 0.55 : 1) + smooth(1, 5, tau) * 4) * sz);
    if (k > 2 + Math.round(sz * 4)) continue;
    const x = w.x + (k - 3) * (w.far ? 2 : 4) + wind * tau * (0.6 + hash(k, 5, 72) * 0.6), y = y0 - up * (0.4 + k * 0.1);
    const alpha = (1 - smooth(1.5, 6.8, tau)) * (w.far ? 0.5 : 0.6);
    if (alpha > 0.02 && r > 0) { ctx.globalAlpha = alpha; ctx.drawImage(PUFF[Math.min(PUFF.length - 1, r)], Math.round(x - r), Math.round(y - r)); }
  }
  // droplets: thrown up, falling back, carried by the wind
  ctx.fillStyle = '#eef6fa';
  for (const d of DROPS) {
    if (w.far && d.d > 0.55) continue;
    const st = tau - d.d * 0.35;
    if (st < 0) continue;
    const vy = d.vy * H * 1.9, x = w.x + d.vx * (w.far ? 0.5 : 1) * st + wind * st * st * 0.5, y = y0 - vy * st + g * st * st * 0.5;
    if (y > y0 + (w.far ? 3 : 18)) continue;
    ctx.globalAlpha = (1 - smooth(2.5, 5.5, st)) * (0.55 + d.d * 0.4);
    ctx.fillRect(Math.round(x), Math.round(y), d.s, d.s);
  }
  ctx.globalAlpha = 1;
  return 1 - smooth(0, 6, tau);
}
// the swell that builds before a big wave: a white crest line coming in across the bay (or along the far wall)
function drawSwell(t, w) {
  const u = (t - (w.t - SWELL)) / SWELL;
  if (u < 0 || u >= 1) return;
  const e = smooth(0, 0.3, u) * (1 - smooth(0.97, 1, u));
  if (w.far) {
    const x1 = lerp(FAR.x0, w.x, u * u), y = farWallY(Math.round(x1)) + 3;
    ctx.globalAlpha = 0.75 * e; rect(ctx, Math.round(x1) - 14, y, 14, 1, '#e8f4f8'); rect(ctx, Math.round(x1) - 22, y + 1, 18, 1, '#9ab8c4');
  } else {
    const y = Math.round(lerp(HZ + 8, WALL - 1, u * u)), len = Math.round(12 + u * 46);
    ctx.globalAlpha = (0.35 + 0.55 * u) * e; rect(ctx, w.x - (len >> 1), y, len, 1, '#e8f4f8');
    ctx.globalAlpha = 0.4 * u * e; rect(ctx, w.x - (len >> 1) + 3, y - 1, len - 6, 1, '#9ab8c4');
  }
  ctx.globalAlpha = 1;
}
// small bursts along the walls, one every 10 s somewhere
function drawSmallSpray(t) {
  const { cyc, u } = cycle(t, 24, 0.13);
  if (u > 0.4) return;
  const far = hash(cyc, 1, 73) < 0.5, x = far ? FAR.x0 + 20 + Math.floor(hash(cyc, 2, 73) * 120) : 50 + Math.floor(hash(cyc, 3, 73) * 330);
  const y0 = far ? farWallY(x) : WALL, tau = u * 24 * 0.4;
  plume(tau, { t: 0, x, far, h: far ? 12 : 16 }, y0);
}
/* ----- the near wall's spray hits: the pavement goes glossy behind it and dries slowly ----- */
function wetness(t, x) {
  let w = 0;
  for (const b of [...BIG.filter(b => !b.far), ...MED]) {
    const tau = t - b.t;
    if (tau > 0 && tau < 30) w = Math.max(w, (1 - smooth(4, 30, tau)) * Math.max(0, 1 - Math.abs(x - b.x - 14) / 40));
  }
  return w;
}
/* ----- spray mist, the city's own weather: fine droplets on the sea wind at three depths ----- */
const MIST = [
  { n: 70, k: 9, y0: HZ - 6, y1: WALL, a: 0.32, s: 1, col: '#c8d8e4' },
  { n: 44, k: 13, y0: 150, y1: PAVE, a: 0.42, s: 1, col: '#dce8f0' },
  { n: 16, k: 19, y0: 120, y1: H, a: 0.5, s: 2, col: '#e8f2f8' },
].map((L, li) => ({ ...L, p: Array.from({ length: L.n }, (_, i) => ({ y: L.y0 + hash(i, li, 74) * (L.y1 - L.y0), ph: hash(i, li + 5, 74), dy: 6 + hash(i, li + 9, 74) * 14 })) }));
function drawMist(t, depth, boost) {
  const L = MIST[depth];
  ctx.fillStyle = L.col;
  for (const q of L.p) {
    const u = frac(t * L.k / LOOP + q.ph), x = Math.round(u * (W + 20) - 10), y = Math.round(q.y + u * q.dy + Math.sin(TAU * (u * 3 + q.ph)) * 2);
    ctx.globalAlpha = L.a * (0.45 + 0.55 * boost) * Math.sin(Math.PI * u);
    ctx.fillRect(x, y, L.s, L.s);
  }
  ctx.globalAlpha = 1;
}
/* ----- traffic: whole crossings a loop in each lane, the cars hidden behind the corner building past x 398 ----- */
const TRAFFIC = [
  { car: 0, east: true, k: 5, ph: 0.02 }, { car: 2, east: true, k: 5, ph: 0.37 }, { car: 3, east: true, k: 5, ph: 0.69 },
  { car: 1, east: false, k: 4, ph: 0.12 }, { car: 4, east: false, k: 4, ph: 0.6 },
];
function carX(c, t) { const u = frac(t * c.k / LOOP + c.ph); return c.east ? -CAR_W - 6 + u * (BLD.x0 + CAR_W + 12) : BLD.x0 + 6 - u * (BLD.x0 + CAR_W + 12); }
/* ----- walkers on the seaside pavement, crossing whole times a loop ----- */
const WALKERS = [
  { k: 3, ph: 0.4, dir: 1, rank: 0.3, P: look(23, 2, 0, TOPS[10], BOTS[2]), mate: look(22, 3, 1, TOPS[1], BOTS[1], { long: 2, skirt: '#d8404a' }) },
  { k: 2, ph: 0.55, dir: -1, rank: 0, P: look(23, 4, 0, '#e8e0d0', BOTS[5], { hat: '#e8dcc0' }), prop: 'mani' },
  { k: 6, ph: 0.8, dir: -1, rank: 0.55, P: look(22, 1, 0, '#f2c84a', BOTS[0]) },
  { k: 2, ph: 0.72, dir: 1, rank: 0.45, P: look(22, 0, 4, '#8aa0c8', BOTS[4], { hat: '#2a2a34' }), cane: true },
];

/* ----- per-frame life ----- */
function drawTraffic(t) {
  for (const c of TRAFFIC) {
    const x = Math.round(carX(c, t)), lane = c.east ? LANE_F : LANE_N, top = lane - CAR_H + 1;
    if (x > BLD.x0 + 2 || x < -CAR_W - 4) continue;
    ctx.globalAlpha = 0.5; R(x + 3, lane + 1, CAR_W - 6, 1, '#06050a'); ctx.globalAlpha = 1;
    ctx.drawImage(CAR_C[c.car][c.east ? 0 : 1], x, top);
    const fx = c.east ? x + CAR_W : x - 1, rx = c.east ? x : x + CAR_W - 1;
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.55;
    ctx.drawImage(c.east ? BEAM_R : BEAM_L, c.east ? fx : fx - BEAM_R.width + 1, top + 4);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    halo(HL.head, fx, top + 7, 0.9); halo(HL.tail, rx, top + 6, 0.8);
  }
}
function renderBeam(flip) {                                      // a headlamp's soft throw along the wet road
  const w = 34, h = 9, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const u = i / w, spread = 1 + u * 3.6, d = Math.abs(y - 2.5 - u * 2) / spread;
    if (d < 1 && (1 - d) * (1 - u) * 1.4 > bayer(i, y)) p.put(flip ? w - 1 - i : i, y, '#6a5a3a');
  }
  return p.done();
}
function drawWalkers(t, crowd) {
  const st = step(t, 768) & 1;
  for (const w of WALKERS) {
    if (w.rank >= crowd) continue;
    const u = frac(t * w.k / LOOP + w.ph), x = w.dir > 0 ? -16 + u * (BLD.x0 + 20) : BLD.x0 + 4 - u * (BLD.x0 + 20);
    const s = (step(t, w.k > 4 ? 1152 : 768) + (w.dir > 0 ? 0 : 1)) & 1;
    person(x, WALK_Y, w.P, { view: 2, dir: w.dir, step: s, aR: w.prop ? 3 : 0 });
    if (w.mate) person(x + 8 * w.dir, WALK_Y, w.mate, { view: 2, dir: w.dir, step: 1 - s });
    if (w.prop === 'mani') { const bx = Math.round(x) + (w.dir > 0 ? 3 : -8); R(bx, WALK_Y - 12, 5, 3, '#a8784a'); R(bx, WALK_Y - 13, 1, 1, '#f4f0e6'); R(bx + 2, WALK_Y - 14, 1, 2, '#f4f0e6'); R(bx + 4, WALK_Y - 13, 1, 1, '#f4f0e6'); }
    if (w.cane) { const cx = Math.round(x) + w.dir * 3; R(cx, WALK_Y - 9, 1, 10, '#6a4a2a'); }
    void st;
  }
}
// people on the wall: verbs on periods that divide the loop, and the flinch when a big wave hits near them
function flinch(t, x) {
  for (const b of BIG) if (!b.far && Math.abs(x - b.x) < 30 && t > b.t - 1.2 && t < b.t + 3.5) return true;
  return false;
}
function drawCast(t, crowd) {
  CAST.forEach(c => {
    if (c.rank >= crowd) return;
    const o = { ...c.o }, ph = c.x * 0.013;
    if (flinch(t, c.x)) { o.aL = 1; o.aR = 1; o.lean = (c.o.lean || 0) + (c.x < 200 ? 1 : -1); }
    else if (c.prop === 'guitar') o.aR = (step(t, 480) & 1) ? 3 : 4;                                    // strumming
    else if (c.act === 'chat') o.aR = frac(t / 6 + ph) < 0.3 ? 3 : 0;
    else if (c.act === 'lean') o.lean = frac(t / 40 + 0.2) < 0.25 ? -2 : c.o.lean;
    else if (c.act === 'clap') o.aL = o.aR = frac(t / 8 + ph) < 0.12 && (step(t, 960) & 1) ? 4 : 0;
    if (o.dangle) o.dangle = c.o.dangle - (frac(t / 3 + ph) < 0.5 ? 1 : 0);
    const hy = person(c.x, c.y, c.P, o);
    if (c.prop === 'line') {
      const hauling = t > FISH[0] && t < FISH[1], q = hauling ? (t - FISH[0]) / (FISH[1] - FISH[0]) : 0;
      const len = hauling ? Math.round(9 * (1 - q)) : 9, wob = Math.round(wave(t, 40, 0.3));
      for (let k = 0; k < len; k++) px(ctx, c.x + 3 + k, hy + 6 - Math.round(k * 0.7) + (k > 5 ? wob : 0), k < 2 ? '#d8d0c0' : '#9aa0a8');
      if (hauling && q > 0.6) { R(c.x + 3, hy + 2, 3, 1, '#c8d8e8'); px(ctx, c.x + 6, hy + 1 + (step(t, 2400) & 1), '#c8d8e8'); }
      R(c.x - 9, SEAT - 4, 4, 4, '#4a6a8a'); R(c.x - 9, SEAT - 4, 4, 1, '#8ab0c8');
    } else if (c.prop === 'guitar') {
      R(c.x + 1, SEAT - 7, 4, 4, '#b8743a'); R(c.x + 2, SEAT - 6, 1, 1, '#2a1a14'); R(c.x + 5, SEAT - 9, 4, 1, '#6a4428'); R(c.x + 9, SEAT - 10, 1, 2, '#3a2418');
    } else if (c.o.aR === 2) halo(HL.phone, c.x + 3, hy, 0.9);
  });
}
function drawBalcony(t, crowd) {
  // the old woman rocking in her chair (4 s a rock), fanning herself, in the open door's light
  const rock = wave(t, 60), dx = Math.round(rock * 0.8), { y } = ROCKER, x = ROCKER.x + dx;
  R(x - 5 - (rock > 0.3 ? 1 : 0), y - 12, 1, 13, '#4a2618'); R(x + 5 - (rock > 0.3 ? 1 : 0), y - 12, 1, 13, '#4a2618'); R(x - 5, y - 13, 11, 1, '#6a3a26');
  if (crowd > 0) {
    const hy = person(x, y, GRANNY, { sit: 1, view: 1, dangle: 5, aR: 4 });
    const f = step(t, 720) % 3;
    R(x + 4, hy + 1 + f, 3, 3, '#e8483a'); R(x + 5, hy + 2 + f, 1, 1, '#ffd23f');
  }
  R(x - 6, y, 13, 1, '#6a3a26'); R(x - 5, y + 1, 1, 6, '#4a2618'); R(x + 5, y + 1, 1, 6, '#4a2618');
  R(ROCKER.x - 7, y + 7 - (rock > 0.3 ? 1 : 0), 15, 1, '#5a2e1e');
  ctx.drawImage(RAIL_C, 0, 0);
}
function drawPortal(t, crowd) {
  const slap = frac(t / 12);                                       // a tile slapped down every 12 s
  PORTAL_CAST.forEach((c, i) => {
    if (crowd <= 0 || (i === 0 && crowd < 0.5)) return;
    const o = { ...c.o };
    if (i === 1 && slap > 0.86 && slap < 0.95) o.aR = 4;
    if (i === 2 && frac(t / 12 + 0.5) > 0.86 && frac(t / 12 + 0.5) < 0.95) o.aR = 4;
    if (i === 4 && frac(t / 20) < 0.2) o.aR = 4;                  // the server pours a cafecito
    person(c.x, c.y, c.P, o);
  });
  R(409, 195, 15, 2, '#5a3a26'); R(409, 195, 15, 1, '#8a5a3a');
  const tiles = 5 + Math.floor(frac(t / 60) * 5);
  for (let k = 0; k < tiles; k++) R(410 + k * 2 - (k > 6 ? 13 : 0), 194 - (k > 6 ? 0 : 0), 1, 1, '#f4f0e6');
  R(410, 197, 1, PAVE - 197, '#3a2418'); R(422, 197, 1, PAVE - 197, '#3a2418');
  for (const sx of [405, 429]) { R(sx - 2, PORTAL_Y - 4, 4, 1, '#6a4428'); R(sx - 2, PORTAL_Y - 3, 1, 4, '#3a2418'); R(sx + 1, PORTAL_Y - 3, 1, 4, '#3a2418'); }
  ctx.drawImage(COUNTER_C, BAYS[1] + 1, 194);
  if (slap > 0.94 && slap < 0.97) px(ctx, 416, 193, '#ffffff');
}
function drawBoat(t) {                                           // a fishing boat out of the harbour mouth, past the Morro
  if (t < BOAT.t0 || t >= BOAT.t1) return;
  const u = (t - BOAT.t0) / (BOAT.t1 - BOAT.t0), x = Math.round(lerp(236, 18, u)), y = MORRO.water + 2 + Math.round(u * 2);
  const a = smooth(0, 0.06, u) * (1 - smooth(0.92, 1, u));
  ctx.globalAlpha = a;
  ctx.drawImage(BOAT_C, x - 9, y - 9);
  ctx.globalAlpha = 1;
  halo(HL.boatLamp, x - 3, y - 8, a * (0.8 + 0.2 * wave(t, 240))); px(ctx, x - 3, y - 8, '#fff6d8');
  px(ctx, x - 8, y - 4, '#ff4a3a'); halo(HL.tail, x - 8, y - 4, a * 0.7);
  ctx.globalAlpha = 0.35 * a; rect(ctx, x - 10, y + 1, 20, 1, '#c8dce4'); ctx.globalAlpha = 1;   // its wake
}
function drawPigeons(t) {
  if (t < PIGEONS.t0 || t >= PIGEONS.t1) return;
  const tau = t - PIGEONS.t0, span = PIGEONS.t1 - PIGEONS.t0;
  for (let i = 0; i < 8; i++) {
    const a = TAU * (tau / 10) + i * 0.32, rise = smooth(0, 3, tau) * (1 - smooth(span - 3, span, tau));
    const x = Math.round(lerp(416, PIGEONS.cx + Math.cos(a) * (30 + (i % 3) * 5), rise));
    const y = Math.round(lerp(39, PIGEONS.cy + Math.sin(a) * 9 - (i % 2) * 3, rise));
    const fl = (step(t, 1920) + i) & 1;
    R(x, y, 1, 1, '#c8c8d0'); R(x - 1, y - fl, 1, 1, '#a8a8b8'); R(x + 1, y - fl, 1, 1, '#a8a8b8');
    if (!fl) { R(x - 2, y, 1, 1, '#8a8a98'); R(x + 2, y, 1, 1, '#8a8a98'); }
  }
}
function drawCannon(t, reducedMotion) {                                         // a flash on La Cabaña's ramparts, its smoke drifting
  const tau = t - CANNON.t;
  if (tau < 0 || tau > 14) return;
  if (tau < 0.5 && !reducedMotion) { halo(HL.cannon, CANNON.x, CANNON.y, 1 - tau * 2); R(CANNON.x - 1, CANNON.y - 1, 3, 2, '#fff2c0'); }
  for (let k = 0; k < 4; k++) {
    const r = Math.min(PUFF.length - 1, Math.round(2 + tau * 0.5 + k)), a = (1 - smooth(2, 14, tau)) * 0.45;
    ctx.globalAlpha = a; ctx.drawImage(PUFF_G[r], Math.round(CANNON.x + tau * 1.6 + k * 3 - r), Math.round(CANNON.y - 3 - tau * 0.8 - k * 2 - r));
  }
  ctx.globalAlpha = 1;
}
const TUBES = [{ x: 150, y: 149, ph: 0.1 }, { x: 196, y: 152, ph: 0.6 }];   // fishermen floating on inner tubes, each with a lamp
function drawTubes(t, crowd) {
  if (crowd <= 0.3) return;
  for (const q of TUBES) {
    const x = Math.round(q.x + wave(t, 2, q.ph) * 5), y = Math.round(q.y + wave(t, 40, q.ph) * 0.6);
    R(x - 3, y, 7, 1, '#1a1a24'); R(x - 2, y + 1, 5, 1, '#0e0e16'); R(x - 1, y - 3, 2, 3, '#2a3a5a'); R(x - 1, y - 4, 2, 1, '#3a2418');
    px(ctx, x + 2, y - 2, '#ffd890'); halo(HL.farLamp, x + 2, y - 2, 0.8);
    ctx.globalAlpha = 0.3; R(x - 4, y + 2, 9, 1, '#a8c4cc'); ctx.globalAlpha = 1;
  }
}
const LAMP_BUZZ = [[84, 85.4], [190.2, 191.5]];                  // the third lamp stutters now and then
function lampI(t, i) {
  if (i === 2 && inEvent(t, LAMP_BUZZ)) { const q = hash(31, Math.floor(t * 18)); return q > 0.5 ? 1 : q > 0.25 ? 0.4 : 0.1; }
  return 0.95 + 0.05 * wave(t, 600, i * 0.2);
}

/* ----- small constant life: bats at the floodlit cupola, surf on the Morro's rocks, the laundry, moths, puddles ----- */
const BATS = Array.from({ length: 4 }, (_, i) => ({ k: 7 + i * 2, k2: 11 + i * 3, ph: i * 0.27, rx: 34 + i * 6, ry: 12 + (i % 2) * 6, cy: 52 + i * 9 }));
function drawBats(t) {
  for (const b of BATS) {
    const a = TAU * (b.k * t / LOOP + b.ph), x = Math.round(DX + Math.sin(a) * b.rx), y = Math.round(b.cy + Math.sin(TAU * (b.k2 * t / LOOP + b.ph)) * b.ry);
    const fl = (step(t, 2880) + b.k) & 1;
    R(x, y, 1, 1, '#1a1622'); R(x - 1, y - fl, 1, 1, '#2a2432'); R(x + 1, y - fl, 1, 1, '#2a2432');
  }
}
function drawSurf(t) {
  const s = step(t, 480);
  ctx.fillStyle = '#d8e4ee';
  for (let x = 48; x < 230; x += 2) if (hash(x, s, 75) < 0.28) ctx.fillRect(x + (s & 1), MORRO.water - 1 - (hash(x, s, 76) < 0.3 ? 1 : 0), 1, 1);
}
// the laundry on its line (drawn over RAIL_C's still copy), swaying in the sea wind
const CLOTHES = [[436, 6, 8, '#f0ece2', '#b8b0a4'], [445, 5, 11, '#e0484a', '#a83034'], [454, 7, 6, '#4a7ad0', '#2e5498'], [465, 5, 6, '#f2d04a', '#b8962e']];
function drawLaundry(t) {
  CLOTHES.forEach(([x0, w, h, col, sh], i) => {
    const sw = wave(t, 30, i * 0.21) + 0.6 * wave(t, 74, i * 0.4);
    for (let x = x0; x < x0 + w; x++) {
      const ly = 121 + Math.round(Math.sin((x - 433) / 47 * Math.PI) * 2);
      for (let y = ly + 1; y < ly + 1 + h; y++) {
        const off = Math.round(sw * (y - ly) / h * 1.4);
        R(x + off, y, 1, 1, x === x0 + w - 1 || y === ly + h ? sh : col);
      }
    }
  });
  // the ceiling fan in the open door turns; the lace curtain breathes
  const fx = BAY_C[0], fy = FLOOR1.door + 4, fa = step(t, 2400) % 3;
  R(fx - 3, fy, 7, 1, '#ffd890'); R(fx - 3 + fa, fy, 2, 1, '#4a3024'); R(fx + 1 + fa, fy, 2, 1, '#4a3024');
  R(fx - 4 + Math.round(wave(t, 24) * 0.7), FLOOR1.door + 14, 1, 8, '#fff2dc');
}
const MOTHS = [[BAYS[0] + 13, 168], [BAYS[1] + 13, 168], [CART_BULB.x, CART_BULB.y], [NEAR_LAMP.x, NEAR_LAMP.top]];
function drawMoths(t) {
  MOTHS.forEach(([cx, cy], j) => {
    for (let i = 0; i < 3; i++) {
      const a = TAU * ((31 + i * 13 + j * 7) * t / LOOP + i * 0.33 + j * 0.1), r = 3 + i * 1.5 + (j === 3 ? 3 : 0);
      R(Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a * 1.7) * r * 0.6), 1, 1, '#e8dcc0');
    }
  });
}
// puddles on the pavement: dark mirrors with the lamps' light shivering in them
const PUDDLES = [{ x: 160, y: 236, w: 38 }, { x: 236, y: 252, w: 46 }, { x: 362, y: 260, w: 30 }, { x: 4, y: 246, w: 20 }];
function drawPuddles(t) {
  PUDDLES.forEach((q, i) => {
    for (let r = 0; r < 4; r++) {
      const hw = Math.round(q.w / 2 * Math.sqrt(1 - ((r - 1.5) / 2) ** 2));
      ctx.globalAlpha = 0.55; R(q.x - hw, q.y + r, hw * 2, 1, '#120f18');
      ctx.globalAlpha = 0.5 + 0.3 * wave(t, 90 + i * 17, r * 0.3);
      const gx = Math.round(q.x + wave(t, 52 + i * 9, r * 0.2) * hw * 0.6);
      R(gx - 2, q.y + r, 4, 1, r & 1 ? '#ffb060' : '#ffd890');
    }
  });
  ctx.globalAlpha = 1;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, DOME_C, FAR_C, SEA_C, NEAR_C, LAMP_C, BLD_C, RAIL_C, COUNTER_C, BB_C, BB_AD, CAR_C, VIG_C, FG_C, PITCH, PUFF, PUFF_G, BOAT_C, BEAM_R, BEAM_L;
function prerender() {
  HL = {
    moon: makeHalo(46, '#9ab4ff', 0.2, 1.8), moonCore: makeHalo(15, '#fff2d0', 0.32, 1.5),
    dome: makeHalo(74, '#ffcf8a', 0.14, 1.5), domeCol: makeHalo(40, '#ffc870', 0.2, 1.6), lantern: makeHalo(12, '#fff0b8', 0.45, 1.4),
    lh: makeHalo(10, '#fff2c8', 0.6, 1.3), lhFlash: makeHalo(30, '#fff6dc', 0.5, 1.4), farLamp: makeHalo(4, '#ffb060', 0.5, 1.2),
    lamp: makeHalo(44, '#ff9a40', 0.2, 1.6), lampCore: makeHalo(9, '#ffd890', 0.55, 1.3),
    bulb: makeHalo(18, '#ffc070', 0.32, 1.4), room: makeHalo(16, '#ffb860', 0.18, 1.4), bbLamp: makeHalo(14, '#ffd8a0', 0.4, 1.5),
    nearLamp: makeHalo(70, '#ffb060', 0.16, 1.7), nearCore: makeHalo(12, '#ffe6b0', 0.6, 1.3), pool: makeHalo(34, '#ffb060', 0.12, 1.5),
    cannon: makeHalo(26, '#ffd8a0', 0.6, 1.4), boatLamp: makeHalo(5, '#fff2d0', 0.5, 1.3),
    head: makeHalo(7, '#fff4d0', 0.55, 1.3), tail: makeHalo(4, '#ff3020', 0.5, 1.2), phone: makeHalo(4, '#bfe0ff', 0.4, 1.2),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  WISP_C = WISPS.map(w => renderWisp(w.w, w.h, w.seed));
  DOME_C = renderDome(); FAR_C = renderFar(); SEA_C = renderSea(); NEAR_C = renderNear(); LAMP_C = renderLamps();
  BLD_C = renderBuilding(); RAIL_C = renderRail(); COUNTER_C = renderCounter(); VIG_C = renderVignette(); FG_C = renderForeground();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  CAR_C = CARS.map(c => [renderCar(c), null]);
  CAR_C.forEach((s, i) => { const [c, x] = makeCanvas(CAR_W, CAR_H); x.translate(CAR_W, 0); x.scale(-1, 1); x.drawImage(s[0], 0, 0); s[1] = c; });
  PITCH = makePitch(BB.w, BB.h, { accent: '#ff006e' });
  PUFF = [null]; PUFF_G = [null];
  for (let r = 1; r <= 16; r++) { PUFF.push(makePuff(r, '#f4fafc', '#b8ccd8')); PUFF_G.push(makePuff(r, '#c8c0bc', '#8a8490')); }
  BOAT_C = sprite(BOAT_ROWS, BOAT_KEY); BEAM_R = renderBeam(false); BEAM_L = renderBeam(true);
}
// a dithered soft blob for spray and smoke
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
// the fishing boat: a white hull, a wheelhouse with a lit window, a mast
const BOAT_ROWS = ['.......m..........', '.......m..........', '......hhhh........', '......hyyh........', '..wwwwwwwwwwwwww..', '.wwwwwwwwwwwwwwww.', '..bbbbbbbbbbbbbb..', '...bbbbbbbbbbbb...'];
const BOAT_KEY = { m: '#3a3440', h: '#c8c4bc', y: '#ffd890', w: '#e8e4dc', b: '#2a4a6a' };

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves it out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own
// when left out; crowd: the share of the people shown (the city's own CROWD when left out)
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // sky: stars, the moon, wisps drifting
  rect(ctx, 0, 0, W, H, '#05070f');
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    px(ctx, s.x, s.y, '#e8ecff');
    if (s.big) { px(ctx, s.x - 1, s.y, '#8890b8'); px(ctx, s.x + 1, s.y, '#8890b8'); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  WISPS.forEach((w, i) => { const x = w.x + Math.round(frac(t / LOOP + i * 0.31) * (W + w.w)) - w.w; ctx.drawImage(WISP_C[i], ((x - w.x + W + w.w) % (W + w.w)) - w.w + 0, w.y); });
  // the Morro's beams, then El Capitolio's cupola
  const flash = drawBeam(t);
  const dp = 0.94 + 0.06 * wave(t, 12);
  halo(HL.dome, DX, DR.col - 6, dp); halo(HL.domeCol, DX, DR.col + 8, dp);
  ctx.drawImage(DOME_C, DOME_BOX.x, DOME_BOX.y);
  halo(HL.lantern, DX, DR.lcol + 3, 0.85 + 0.15 * wave(t, 30));
  drawBats(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the Morro, La Cabaña, the Capitolio's body, the far curve, the cannon, the rooftop sign
  ctx.drawImage(FAR_C, 0, 0);
  halo(HL.lh, LH.x + 0.5, 102, 0.8 + flash * 0.2); halo(HL.lhFlash, LH.x + 0.5, 102, reducedMotion ? flash * 0.4 : flash);
  for (const w of FAR_WINS) if (hash(w.id, Math.floor(frac(t / LOOP + w.ph) * w.n), 3) < 0.25) px(ctx, w.x, w.y, '#3a2c34');
  for (const lx of FAR_LAMPS) { const y = farWallY(lx) - 6; px(ctx, lx, y, '#ffd08a'); halo(HL.farLamp, lx, y, 0.9); }
  drawSurf(t);
  drawCannon(t, reducedMotion);
  if (billboard) drawBillboard(t, pitch);
  // the bay: mirrored lights, crests, the swells building, a boat going out, fishermen on their tubes, the spray
  drawSea(t);
  drawBoat(t);
  drawTubes(t, crowd);
  let boost = 0.25;
  for (const b of BIG) drawSwell(t, b);
  for (const b of BIG) if (b.far) boost = Math.max(boost, plume(t, b, farWallY(b.x)));
  drawSmallSpray(t);
  if (wx.spray) drawMist(t, 0, boost);
  // the near wall, its people, the walkers, the lamps
  ctx.drawImage(NEAR_C, 0, WALL);
  for (let x = 40; x < BLD.x0; x += 2) { const w = wetness(t, x); if (w > 0.05) { ctx.globalAlpha = w * 0.35; rect(ctx, x, PROM, 2, KERB - PROM, '#7a6a64'); } }
  ctx.globalAlpha = 1;
  drawCast(t, crowd);
  drawWalkers(t, crowd);
  ctx.drawImage(LAMP_C, 0, 0);
  LAMPS.forEach((lx, i) => {
    const li = lampI(t, i);
    rect(ctx, lx + 5, LAMP_TOP + 1, 3, 1, li > 0.6 ? '#ffe2a0' : '#7a5a3a');
    halo(HL.lamp, lx + 6, LAMP_TOP + 6, li * 0.95); halo(HL.lampCore, lx + 6, LAMP_TOP + 1, li);
    streak(ctx, t, lx + 6, 4, '#ff9a48', 0.3 * li, lx, ROAD0, LANE_N + 1);
  });
  for (const b of BIG) if (!b.far) boost = Math.max(boost, plume(t, b, WALL));
  for (const m of MED) plume(t, m, WALL);
  if (wx.spray) drawMist(t, 1, boost);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawTraffic(t);
  // the corner building, its balcony, portal and the pigeons
  ctx.drawImage(BLD_C, 0, 0);
  halo(HL.room, BAY_C[0], 132, 0.85 + 0.1 * wave(t, 20));
  if (hash(9, step(t, 1200)) < 0.7) R(BAY_C[2] - 1, FLOOR2.door + 4, 2, 20, '#4a6ab8');               // a television behind the louvres
  drawBalcony(t, crowd);
  drawLaundry(t);
  drawPortal(t, crowd);
  for (const b of [0, 1]) { const x = BAYS[b] + 13; rect(ctx, x, 161, 1, 5, '#2a2026'); rect(ctx, x - 1, 166, 3, 2, '#fff2c0'); halo(HL.bulb, x, 168, (b ? 1 : 0.9) * (0.94 + 0.06 * wave(t, 480, b * 0.5))); }
  drawPigeons(t);
  // the wet pavement in front of us mirrors it all, the foreground on it
  reflect(ctx, t, { top: PAVE + 2, rows: 40, squash: 1.2, alpha: 0.15 + boost * 0.06, amp: 0.7, grow: 0.06 });
  drawPuddles(t);
  streak(ctx, t, NEAR_LAMP.x, 6, '#ffc070', 0.35, 21, NEAR_LAMP.foot - 6, H);
  LAMPS.forEach((lx, i) => streak(ctx, t, lx + 6, 4, '#ff9a48', 0.3 * lampI(t, i), lx + 3, PAVE + 3, H));
  streak(ctx, t, BAYS[1] + 13, 6, '#ffc070', 0.3, 9, PAVE + 3, H);
  halo(HL.pool, NEAR_LAMP.x + 6, 250, 1);
  ctx.drawImage(FG_C, 0, 0);
  rect(ctx, NEAR_LAMP.x - 2, NEAR_LAMP.top - 5, 5, 10, '#ffe2a8'); rect(ctx, NEAR_LAMP.x - 1, NEAR_LAMP.top - 4, 3, 8, '#fff6dc');
  halo(HL.nearLamp, NEAR_LAMP.x, NEAR_LAMP.top, 1); halo(HL.nearCore, NEAR_LAMP.x, NEAR_LAMP.top, 1);
  drawForegroundLife(t, crowd);
  drawMoths(t);
  if (wx.spray) drawMist(t, 2, boost);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'havana',
  name: 'Havana',
  tagline: 'Waves over the Malecón',
  timeZone: 'America/Havana',
  accent: '#ff006e',
  focusX: DOME.cx,
  landmark: [DOME.cx - 32, DOME.cx + 32],              // El Capitolio's cupola, its colonnade's full width
  landmarkTop: DOME.top,                               // the finial's tip
  billboard: BB,
  crowd: CROWD,
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: DOME.cx - 31, y: DOME.top, w: 63, h: DR.colB - DOME.top },                // El Capitolio's cupola
    { kind: 'water', x: 60, y: MORRO.water + 4, w: 270, h: WALL - MORRO.water - 8 },                // the bay, to below the cupola
    { kind: 'birds', x: 404, y: 37, w: 26, h: 13 },                                                  // the pigeon loft
  ],
  ambience: { waves: 0.7, wind: 0.35, water: 0.2, traffic: 0.3, crowd: 0.15 },
  events: [
    { t: 20, type: 'splash', x: 62, gain: 0.45 }, { t: 48, type: 'splash', x: 118, gain: 0.9 },
    { t: 64, type: 'horn', x: 230, gain: 0.35 }, { t: 64, type: 'boat', dur: 66, pan: [0, -0.9], gain: 0.25 },
    { t: 88, type: 'splash', x: 300, gain: 0.45 }, { t: 126, type: 'splash', x: 322, gain: 0.6 },
    { t: 160, type: 'splash', x: 172, gain: 0.45 }, { t: 169, type: 'thunder', x: 241, gain: 0.3 },
    { t: 204, type: 'splash', x: 236, gain: 0.9 }, { t: 228, type: 'splash', x: 352, gain: 0.45 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
